/* eslint-disable @typescript-eslint/no-unused-vars */
import React, {Fragment, memo, useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {PasswordContainer} from '../../../../components/PasswordContainer';
import {SettingsPageContainer} from '../../../../components/Settings/SettingsPageContainer';
import {useHistory, useLocation, useParams} from 'react-router-dom';
import {useKeyboard} from '../../../../hooks/keyboard';
import {SettingInputComponent} from '../../../../components/Settings/SettingInputComponent';
import {useDispatch, useSelector} from 'react-redux';
import {DeviceTypes} from "../../../../constants/DeviceTypes";

import {
    AlarmControlParameters, BRIDGE_DEVICE_TYPE_ID_ST, BRIDGE_DEVICE_TYPE_ID_STPM, BRIDGE_DEVICE_TYPE_ID_STS_PLUS,
    CONTROL_TYPE_ID_ALARM, CONTROL_TYPE_ID_ANALOG,
    CONTROL_TYPE_ID_DIMMER, CONTROL_TYPE_ID_ELEVATOR, CONTROL_TYPE_ID_LIGHT,
    CONTROL_TYPE_ID_SIREN,
    CONTROL_TYPE_ID_THERMOSTAT, CONTROL_TYPE_ID_THREE_ANALOG, SOUND_LEVEL_TYPE_ID, SOUND_PLAYED_TYPE_ID,
    ControlModel,
    ControlParameters,
    ControlRenderType,
    ControlTypeModel,
    DeviceParameters,
    DimmerControlParameters,
    ControlThreeAnalog,
    INPUT_TYPE_ID_MOMENTARY,
    INPUT_TYPE_ID_SENSOR,
    IOControlParameters,
    PulseControlParameters,
    SimpleControlParameters,
    SirenControlParameters,
    TemperatureControlSettings,
    ThreePhaseControlParameters,
    ControlSoundAnalog,
    ControlScreenSaver,
    CONTROL_TYPE_SCREEN_SAVER,
    CONTROL_TYPE_ID_FANCOIL,
    ControlFancoilSet,
    ControlNotificationTime, ControlThreeAnalogMap, BRIDGE_DEVICE_TYPE_ID_STPM_F, BRIDGE_DEVICE_TYPE_ID_STP, CONTROL_TYPE_ADV_THERMOSTAT, BRIDGE_DEVICE_TYPE_ID_CCM, CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN, CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON, BRIDGE_DEVICE_TYPE_ID_CCM2312, BRIDGE_DEVICE_TYPE_ID_CCM2324, CONTROL_TYPE_ID_VRF, VRFParameters
    ,CONTROL_TYPE_GENERAL_TEMPERATURE, BRIDGE_DEVICE_TYPE_ID_EIO, KNXThermostatParameters, KNXLampParameters, KNXPlugParameters, KNXDimmerParameters, KNXCurtainParameters, BRIDGE_DEVICE_TYPE_ID_KNX, CONTROL_TYPE_ID_PLUG, CONTROL_TYPE_ID_CURTAIN, KNXFancoilParameters, HideControlParameters,
    CONTROL_TYPE_ID_MKK,
    PasswordControlParameters,
    CONTROL_TYPE_ID_ENERGY,
    CONTROL_TYPE_ID_CAMERA_ENERGY_RELAY,
    CONTROL_TYPE_ID_BELL, BRIDGE_DEVICE_TYPE_ID_FRAME,
    CONTROL_TYPE_ID_EXTERNAL_ALARM,
    CONTROL_TYPE_ID_SENSOR_ENERGY,
    ExternalAlarmParameters,
    SensorEnergyParameters
} from '../../../../types/control';
import {SelectItem} from '../../../../components/Select';
import {BridgeDeviceModel} from '../../../../types/bridge-device';
import {SettingCheckBoxComponent} from '../../../../components/Settings/SettingCheckboxComponent';
import {SettingSelectComponent} from '../../../../components/Settings/SettingSelectComponent';
import {AreaModel} from '../../../../store/control/state';
import {useFilterSelector} from '../../../../hooks/filter-selector';
import {useSetSelector} from '../../../../hooks/set-selector';
import {useMessage} from '../../../../hooks/message';
import {useRPC} from '../../../../hooks/rpc';
import {
    isValidDuration,
    isValidDurationForMDL,
    isValidKNXValue,
    isValidMaxDimmerValue,
    isValidMinDimmerValue,
    isValidPulseDuration,
} from '../../../../utils/validation';

import {CONTROL_WRAPPER, useControlSelector} from '../../../../hooks/control-selector';
import {useGlassControl} from '../../../../hooks/glass-control';
import {useMap} from '../../../../hooks/map';
import {SettingsDeleteButton} from '../../../../components/Settings/SettingsDeleteButton';
import {useLocalization} from '../../../../hooks/localization';
import { changeBusyAction } from '../../../../store/ui/actions';
let timeout;

const BACK_URL = '/settings/control';
const INPUT_EDIT_URL = '/settings/input/edit';

const CONTROL_TYPE_VALUE_MAP: Record<ControlRenderType, SelectItem<number>[]> = {
    complex: [
        {
            value: 0,
            label: 'close_stop',
        },
        {
            value: 1,
            label: 'open_up',
        },
        {
            value: 2,
            label: 'down',
        },
        {
            value: 3,
            label: 'open_close',
        },
    ],
    simple: [
        {
            value: 0,
            label: 'close',
        },
        {
            value: 1,
            label: 'open',
        },
    ],
    three_phase: [
        {
            value: 0,
            label: 'stop',
        },
        {
            value: 1,
            label: 'up',
        },
        {
            value: 2,
            label: 'down',
        },
    ],
    two_phase: [
        {
            value: 1,
            label: 'up',
        },
        {
            value: 2,
            label: 'down',
        },
    ],
    pulse: [
        {
            value: 0,
            label: 'passive',
        },
        {
            value: 1,
            label: 'active',
        },
    ],
};

export const NO_OUTPUT_OPTION: SelectItem<number> = {value: 99, label: 'do_not_output'};

type ALARM_CONTROL_VALUE = {
    id: string;
    isVirtual: boolean;
};

export const ControlEditScreen = memo(function ControlEditScreen() {
    const dispatch = useDispatch();
    const isBusy = useSelector(state => state.ui.busy)
    const {t} = useLocalization();
    const params = useParams<{ id: string }>();
    const {success, failure} = useMessage();
    const [rpc] = useRPC();
    const history = useHistory();
    const location = useLocation<ControlModel>();
    const [setActiveInput, renderKeyboard, currentInput] = useKeyboard();
    const [setActiveNumpadInput, renderNumpadKeyboard, currentNumpadInput] = useKeyboard('numpad', true);
    const [setActiveNumpadExtendedInput, renderNumpadExtendedKeyboard, currentNumpadExtendedInput] =
        useKeyboard('numpad-extended', true);

    const controlList = useControlSelector();
    const controlSet = useSetSelector(state => state.control.controlList);
    const controlTypeSet = useSetSelector(state => state.control.controlTypeList);
    const bridgeDeviceTypeSet = useSetSelector(state => state.control.bridgeDeviceTypeList);
    const bridgeDeviceSet = useSetSelector(state => state.control.bridgeDeviceList);
    const bridgeDeviceList = useSelector(state => state.control.bridgeDeviceList);



    const bridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] =
        useMemo(() => bridgeDeviceList
            .filter(bridgeDevice => (bridgeDeviceTypeSet[bridgeDevice.type_id]?.output_count || 0) > 0)
            .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
            .map(bridgeDevice => ({
                value: bridgeDevice,
                label: bridgeDevice.name,
            })), [bridgeDeviceList, bridgeDeviceTypeSet]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selected = useMemo(() => location.state || controlSet[params?.id] || null, []);
    const [bridgeDevice, setBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        (selected == null ? null :
            bridgeDeviceOptionList.find(bridgeDeviceOption => bridgeDeviceOption.value.id === selected.bridge_device_id)
            || null),
    );

    const virtualSet = useSetSelector(state => state.control.virtualControlList)
    // eslint-disable-next-line react-hooks/exhaustive-deps
    const selectedRemoteControl = useMemo(() => virtualSet[params?.id] || null, [])

    const bridgeDeviceType = useMemo(() => bridgeDevice == null ? null : bridgeDeviceTypeSet[bridgeDevice.value.type_id],
        [bridgeDevice, bridgeDeviceTypeSet]);

    const remoteBridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] =
        useMemo(() => bridgeDeviceList
            .filter(selectedBridgeDevice => (bridgeDeviceTypeSet[selectedBridgeDevice.type_id]?.output_count || 0) > 0 && selectedBridgeDevice.id !== bridgeDevice?.value?.id)
            .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
            .map(selectedBridgeDevice => ({
                value: selectedBridgeDevice,
                label: selectedBridgeDevice.name,
            })), [bridgeDeviceList, bridgeDeviceTypeSet, bridgeDevice]);

    const [controlBridgeDevice, setControlBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
       (selectedRemoteControl == null ? null :
            remoteBridgeDeviceOptionList.find(bridgeDeviceOption => bridgeDeviceOption.value.id === bridgeDeviceSet[controlSet[selectedRemoteControl?.control_id]?.bridge_device_id]?.id)
            || null),
    );

    const controlOptionList = useMap(useFilterSelector(
        state => state.control.controlList,
        control => controlBridgeDevice?.value?.id === control.bridge_device_id,
        [controlBridgeDevice?.value?.id],
    ), control => ({
        value: control,
        label: control.name,
    }));

    const [control, setControl] = useState<SelectItem<ControlModel>>(
        (selectedRemoteControl == null ? null :
            controlOptionList.find(option => option.value.id === selectedRemoteControl.control_id) || null),
    );

    const openCloseOptionList: SelectItem<number>[] = [
        {label: t('open'), value: 1},
        {label: t('close'), value: 0}
    ]
    const [alarmDisableValue, setAlarmDisableValue] = useState<SelectItem<number | undefined>>(
        selected == null ? openCloseOptionList[0] : (
            openCloseOptionList.find(option => option.value === (selected.parameters as SensorEnergyParameters)?.alarm_disable_value)
            || openCloseOptionList[0])
    )
    const [alarmArmedValue, setAlarmArmedValue] = useState<SelectItem<number>>(
        selected == null ? openCloseOptionList[0] : (
            openCloseOptionList.find(option => option.value === (selected.parameters as ExternalAlarmParameters)?.alarm_set_value)
            || openCloseOptionList[0])
    )
    const bridgeDeviceDimmerCount = useMemo(() => bridgeDeviceType?.dimmer_count || 0, [bridgeDeviceType]);
    const isBridgeDeviceTypeIO = useMemo(() => bridgeDeviceType?.is_io_device || false, [bridgeDeviceType]);
    const usedOutputList = useMemo(() => {
        const list = [];
        for (const control of Object.values(controlSet)) {
            if (control.bridge_device_id !== bridgeDevice?.value.id) {
                continue;
            }

            list.push(control?.parameters?.output_number);

            if (controlTypeSet[control.type_id].render_type === 'three_phase' || controlTypeSet[control.type_id].render_type === 'two_phase') {
                list.push((control.parameters as ThreePhaseControlParameters).down_output_number);
            }
            if (control.type_id === CONTROL_TYPE_ID_FANCOIL) {
                list.push(
                    (control?.parameters as ControlFancoilSet)?.fan_output_low,
                    (control?.parameters as ControlFancoilSet)?.fan_output_medium,
                    (control?.parameters as ControlFancoilSet)?.fan_output_high
                )
            }
        }

        return list;
    }, [bridgeDevice, controlSet, controlTypeSet]);

    const alarmControlOptionList: SelectItem<CONTROL_WRAPPER>[] = useMemo(() => {
            const controls = controlList
                .filter(control => control.bridgeDeviceId === bridgeDevice?.value?.id && control?.control?.type_id !== CONTROL_TYPE_ID_ALARM && control?.control?.type_id !== CONTROL_TYPE_ID_SIREN)
                .map(control => ({label: control.control.name, value: control}));

            controls.unshift({
                label: t('do_not_output'), value: {
                    id: undefined,
                    isVirtual: false,
                    bridgeDeviceId: '',
                    glassControlParameters: undefined,
                    control: undefined,
                },
            });

            return controls;
        },
        [t, controlList, bridgeDevice?.value]);
    const outputNumberOptionList: SelectItem<number>[] = useMemo(() => {
        const outputCount = bridgeDeviceType?.output_count || 0;
        if (outputCount === 0) {
            return [];
        }

        const list = [];
        list.push({...NO_OUTPUT_OPTION, label: t(NO_OUTPUT_OPTION.label)});
        for (let i = 0; i < outputCount; i++) {
            //@ts-ignore
            const isUsedOutput = usedOutputList.some((o) => o === i && (o !== selected?.parameters?.output_number && o !== selected?.parameters?.down_output_number && o !== (selected?.parameters as ControlFancoilSet)?.fan_output_low && o !== (selected?.parameters as ControlFancoilSet)?.fan_output_medium && o !== (selected?.parameters as ControlFancoilSet)?.fan_output_high));
            list.push({value: i, label: `${t('output')} ${i.toString()}${isUsedOutput ? '*' : ''}`});
        }

        return list;
    }, [t, bridgeDeviceType, usedOutputList, selected?.parameters]);


    const doubledOutputNumberOptionList: SelectItem<number>[] = useMemo(() => {
        const outputCount = bridgeDeviceType?.output_count || 0;
        if (outputCount === 0) {
            return [];
        }

        const list = [];
        list.push({...NO_OUTPUT_OPTION, label: t(NO_OUTPUT_OPTION.label)});
        for (let i = 0; i + 1 < outputCount; i += 2) {
            //@ts-ignore
            const isUsedOutput = usedOutputList.some((o) => o === i && (o !== selected?.parameters?.output_number && o !== selected?.parameters?.down_output_number));
            list.push({
                value: i,
                label: `${t('output')} ${i.toString()} - ${(i + 1).toString()}${isUsedOutput ? '*' : ''}`,
            });
        }

        return list;
    }, [t, bridgeDeviceType, usedOutputList, selected?.parameters]);

    const controlTypeList = useSelector(state => state.control.controlTypeList);
    const controlTypeOptionList: SelectItem<Omit<ControlTypeModel, 'image_set'>>[] =
        useMemo(() => controlTypeList
            .filter(controlType =>
                controlType.allowed_bridge_device_type_id_list.indexOf(bridgeDevice?.value?.type_id) !== -1)
            .filter(controlType =>
                controlType.id !== CONTROL_TYPE_ID_DIMMER || bridgeDeviceDimmerCount > 0)
            .sort((a, b) => a.name < b.name ? -1 : a.name > b.name ? 1 : 0)
            .map(controlType => ({
                value: controlType,
                label: controlType.name,
            })), [bridgeDeviceDimmerCount, bridgeDevice?.value?.type_id, controlTypeList]);

    const [name, setName] = useState(selected?.id != null ? selected?.name : '');
    const [controlType, setControlType] = useState<SelectItem<Omit<ControlTypeModel, 'image_set'>>>(
        (selected == null ? null : controlTypeOptionList.find(controlTypeOption => controlTypeOption.value.id === selected.type_id)
            || null),
    );
    const [outputReverse, setOutputReverse] = useState((selected?.parameters as SimpleControlParameters)?.should_output_reverse || false);
    const [notificationReverse, setNotificationReverse] = useState((selected?.parameters as ControlNotificationTime)?.is_notification || false);
    const [pickedTimeStringBeginning, setPickedTimeStringBeginning] = useState((selected?.parameters as ControlNotificationTime)?.start_time || "")
    const [pickedTimeStringFinish, setPickedTimeStringFinish] = useState((selected?.parameters as ControlNotificationTime)?.end_time || "")
    const [rememberLastValue, setRememberLastValue] = useState((selected?.parameters as IOControlParameters)?.should_remember_last_value || false);
    const [hideControl, setHideControl] = useState<boolean>((selected?.parameters as HideControlParameters)?.hide_control || false);
    const [passwordControl, setPasswordControl] = useState<boolean>((selected?.parameters as PasswordControlParameters)?.password_control || false);
    const [navigateToSTPEditor, setNavigateToSTPEditor] = useState<boolean>();
    const [navigateToFrameEditor, setNavigateToFrameEditor] = useState<boolean>();


    const [knxLampOutputAddress, setKnxLampOutputAddress] = useState((selected?.parameters as KNXLampParameters)?.lamp_output_address || '')
    const [knxLampFeedbackAddress, setKnxLampFeedbackAddress] = useState((selected?.parameters as KNXLampParameters)?.lamp_feedback_address || '')

    const [knxPlugOutputAddress, setKnxPlugOutputAddress] = useState((selected?.parameters as KNXPlugParameters)?.plug_output_address || '')
    const [knxPlugFeedbackAddress, setKnxPlugFeedbackAddress] = useState((selected?.parameters as KNXPlugParameters)?.plug_feedback_address || '')

    const [knxDimmerOutputAddress, setKnxDimmerOutputAddress] = useState((selected?.parameters as KNXDimmerParameters)?.dimmer_output_address || '')
    const [knxDimmerFeedbackAddress, setKnxDimmerFeedbackAddress] = useState((selected?.parameters as KNXDimmerParameters)?.dimmer_feedback_address || '')

    const [knxCurtainStopAddress, setKnxCurtainStopAddress] = useState((selected?.parameters as KNXCurtainParameters)?.blinds_stop_address || '')
    const [knxCurtainUpDownAddress, setKnxCurtainUpDownAddress] = useState((selected?.parameters as KNXCurtainParameters)?.blinds_up_down_address || '')

    const [knxThermostatCoolingFeedbackAddress, setKnxThermostatCoolingFeedbackAddress] = useState(
        (selected?.parameters as KNXThermostatParameters)?.thermostat_cooling_mode_feedback_address || ""
    )
    const [knxThermostatHeatingFeedbackAddress, setKnxThermostatHeatingFeedbackAddress] = useState(
        (selected?.parameters as KNXThermostatParameters)?.thermostat_heating_mode_feedback_address || ""
    )
    const [knxThermostatCurrentSetpointFeedbackAddress, setKnxThermostatCurrentSetpointFeedbackAddress] = useState(
        (selected?.parameters as KNXThermostatParameters)?.thermostat_current_setpoint_feedback_address || ""
    )
    const [knxThermostatCurrentTempFeedbackAddress, setKnxThermostatCurrentTempFeedbackAddress] = useState(
        (selected?.parameters as KNXThermostatParameters)?.thermostat_current_temp_feedback_address || ""
    )
    const [knxThermostatModeFeedbackAddress, setKnxThermostatModeFeedbackAddress] = useState(
        (selected?.parameters as KNXThermostatParameters)?.thermostat_mode_feedback_address || ""
    )
    const [knxThermostatModeOutputAddress, setKnxThermostatModeOutputAddress] = useState(
        (selected?.parameters as KNXThermostatParameters)?.thermostat_mode_output_address || ""
    )
    const [knxThermostatSetpointOutputAddress, setKnxThermostatSetpointOutputAddress] = useState(
        (selected?.parameters as KNXThermostatParameters)?.thermostat_setpoint_output_address || ""
    )

    const [fancoilHeatingModeFeedback, setFancoilHeatingModeFeedback] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_heating_mode_fan_feedback_address || "");

    const [fancoilCoolingModeFeedback, setFancoilCoolingModeFeedback] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_cooling_mode_fan_feedback_address || "");

    const [fancoilCurrentSetpointFeedback, setFancoilCurrentSetpointFeedback] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_current_setpoint_feedback_address || "");

    const [fancoilCurrentSetpointOutput, setFancoilCurrentSetpointOutput] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_current_setpoint_output_address || "");

    const [fancoilAutoFanFeedback, setFancoilAutoFanFeedback] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_fan_auto_fan_feedback_address || "");

    const [fancoilCurrentTempFeedback, setFancoilCurrentTempFeedback] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_current_temp_feedback_address || "");

    const [fancoilAutoFanOutput, setFancoilAutoFanOutput] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_fan_auto_fan_output_address || "");

    const [fancoilFanSpeedFeedback, setFancoilFanSpeedFeedback] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_fan_speed_feedback_address || "");

    const [fancoilFanSpeedOutput, setFancoilFanSpeedOutput] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_fan_speed_output_address || "");

    const [fancoilStatusFeedback, setFancoilStatusFeedback] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_status_feedback_address || "");

    const [fancoilStatusOutput, setFancoilStatusOutput] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_status_output_address || "");

    const [fancoilModeFeedback, setFancoilModeFeedback] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_mode_feedback_address || "");

    const [fancoilModeOutput, setFancoilModeOutput] = useState((selected?.parameters as KNXFancoilParameters)?.fancoil_mode_output_address || "");


    const advancedThermostatBridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMap(
        useFilterSelector(
            state => state.control.bridgeDeviceList,
            bridgeDeviceItem =>
                bridgeDeviceTypeSet[bridgeDeviceItem.type_id]?.is_io_device === true &&
                controlType?.value?.id === CONTROL_TYPE_ADV_THERMOSTAT &&
                (bridgeDeviceItem.type_id === '20' || bridgeDeviceItem.type_id === BRIDGE_DEVICE_TYPE_ID_FRAME),
            [bridgeDeviceTypeSet, controlType, controlList]
        ),
        bridgeDevice => ({value: bridgeDevice, label: bridgeDevice.name}),
        []
    )

    const thermostatBridgeDeviceOptionList: SelectItem<BridgeDeviceModel>[] = useMap(
        useFilterSelector(
            state => state.control.bridgeDeviceList,
            bridgeDeviceItem =>
                bridgeDeviceTypeSet[bridgeDeviceItem.type_id]?.is_temperature_device === true &&
                bridgeDevice?.value != null &&
                bridgeDevice.value.gateway_id === bridgeDeviceItem.gateway_id &&
                controlList.find(c =>
                    c.isVirtual === false &&
                    (c.control.temperature_settings?.bridge_device_id === bridgeDeviceItem.id && bridgeDeviceItem.id !== selected?.temperature_settings?.bridge_device_id)) == null,
            [bridgeDeviceTypeSet, bridgeDevice?.value, controlList, selected],
        ),
        bridgeDevice => ({value: bridgeDevice, label: bridgeDevice.name}),
        [],
    );

    const thermostatOptionList: SelectItem<BridgeDeviceModel>[] = useMemo(() => {
            if(controlType?.value?.id === CONTROL_TYPE_ADV_THERMOSTAT && isBridgeDeviceTypeIO){
                return [...advancedThermostatBridgeDeviceOptionList]
            }else{
                return [...thermostatBridgeDeviceOptionList]
            }
        },
        [advancedThermostatBridgeDeviceOptionList, thermostatBridgeDeviceOptionList, controlType?.value, isBridgeDeviceTypeIO]
    )

    const [thermostatBridgeDevice, setThermostatBridgeDevice] = useState<SelectItem<BridgeDeviceModel>>(
        (selected == null ? null :
            thermostatBridgeDeviceOptionList.find(b => b.value.id === selected.temperature_settings?.bridge_device_id)
            || null),
    );

    const thermostatBridgeDeviceType = useMemo(() => thermostatBridgeDevice == null ? null : bridgeDeviceTypeSet[thermostatBridgeDevice.value.type_id],
        [thermostatBridgeDevice, bridgeDeviceTypeSet]);

    const isThermostatBridgeDeviceIO = useMemo(() => thermostatBridgeDeviceType == null || thermostatBridgeDeviceType?.is_io_device, [thermostatBridgeDeviceType]);

    const SCREEN_CONTROL_LIST = useMemo<SelectItem<number>[]>(() => [
        {
            value: 0,
            label: t('digital'),
        },
        {
            value: 1,
            label: t('analog'),
        },
        {
            value: 2,
            label: t('screen_saver'),
        },
    ], [t]);
    const [screenSaver, setScreenSaver] = useState<SelectItem<number>>(
        selected == null ? SCREEN_CONTROL_LIST[0] : SCREEN_CONTROL_LIST.find((screenControl) => screenControl.value === (selected.parameters as ControlScreenSaver)?.screen_type) || null
    );

    const SOUND_CONTROL_DEVICE = useMemo<SelectItem<number>[]>(() => [
        {
            value: 0,
            label: t('private'),
        },
        {
            value: 1,
            label: t('scenario_sound_1'),
        },
        {
            value: 2,
            label: t('scenario_sound_2'),
        },
        {
            value: 3,
            label: t('scenario_sound_3'),
        },
        {
            value: 4,
            label: t('scenario_sound_4'),
        }
    ], [t]);
    const [soundControl, setSoundControl] = useState<SelectItem<number>>(
        selected == null ? SOUND_CONTROL_DEVICE[0] : SOUND_CONTROL_DEVICE.find((soundControl) => soundControl.value === (selected.parameters as ControlSoundAnalog)?.sound_type) || null
    );

    const [addInput, setAddInput] = useState(false);
    const [isRemoteControl, setIsRemoteControl] = useState(selectedRemoteControl?.parameters?.['is_remote_control'] || false);


    const {
        buttonNumberOptionList,
        setButtonNumber,
        buttonNumber,
    } =
        useGlassControl(selected, bridgeDevice?.value?.id || null, bridgeDeviceType, controlType?.value?.id);

    const areaList = useFilterSelector(state => state.control.areaList, area => area.type === 'room' || area.type === 'place', []);
    const areaOptionList: SelectItem<AreaModel>[] =
        useMemo(() => areaList.map(area => ({value: area, label: area.name})), [areaList]);
    const [areaOption, setAreaOption] = useState<SelectItem<AreaModel>>(
        (selected == null ? null : areaOptionList.find(areaOption => areaOption.value.id === selected.area_id)
            || null),
    );

    const RGB_LIST = useMemo<SelectItem<number>[]>(
      () => [
        {
          value: 0,
          label: "0",
        },
        {
          value: 1,
          label: "1",
        },
        {
          value: 2,
          label: "2",
        },
      ],
      []
    );
    const [rgbListOptionRed, setRgbListOptionRed] = useState<SelectItem<number>>(
        RGB_LIST.find((rgbOption) => rgbOption.value === (selected?.parameters as ControlThreeAnalogMap)?.map_r) || null
    );
    const [rgbListOptionGreen, setRgbListOptionGreen] = useState<SelectItem<number>>(
        RGB_LIST.find(rgbOption => rgbOption.value === (selected?.parameters as ControlThreeAnalogMap)?.map_g) || null
    )
    const [rgbListOptionBlue, setRgbListOptionBlue] = useState<SelectItem<number>>(
        RGB_LIST.find(rgbOption => rgbOption.value === (selected?.parameters as ControlThreeAnalogMap)?.map_b) || null
    )

    const [outputNumber, setOutputNumber] = useState<SelectItem<number>>(
        (selected == null ||
        (!isBridgeDeviceTypeIO && (controlType?.value.render_type === 'three_phase' || controlType?.value.render_type === 'two_phase')) ? null :
            outputNumberOptionList.find(outputNumberOption => outputNumberOption.value === selected.parameters?.output_number)
            || null),
    );
    const [downOutputNumber, setDownOutputNumber] = useState<SelectItem<number>>(
        (selected == null ||
        (!isBridgeDeviceTypeIO && (controlType?.value.render_type === 'three_phase' || controlType?.value.render_type === 'two_phase')) ? null :
            outputNumberOptionList.find(outputNumberOption => outputNumberOption.value === (selected.parameters as ThreePhaseControlParameters)?.down_output_number)
            || null),
    );
    const [doubledOutputNumber, setDoubledOutputNumber] = useState<SelectItem<number>>(
        (selected == null || isBridgeDeviceTypeIO || (controlType?.value.render_type !== 'three_phase' && controlType?.value.render_type !== 'two_phase') ? null :
            doubledOutputNumberOptionList.find(outputNumberOption => outputNumberOption.value === selected.parameters?.output_number)
            || null),
    );
    const [outputDurationLimitSeconds, setOutputDurationLimitSeconds] = useState(
        (selected?.parameters as ThreePhaseControlParameters)?.output_time_limit?.toString()
        || '');
    const [pulseDuration, setPulseDuration] = useState(
        (((selected?.parameters as PulseControlParameters)?.pulse_time || 0) / 10).toString()
        || '');
    const [maxValue, setMaxValue] = useState((selected?.parameters as DimmerControlParameters)?.max_limit?.toString()
        || '100');
    const [minValue, setMinValue] = useState((selected?.parameters as DimmerControlParameters)?.min_limit?.toString()
        || '0');
    const [unitID, setUnitID] = useState((selected?.parameters as VRFParameters)?.unit_id?.toString() || "0")

    const ALARM_LEVEL_LIST = useMemo(() => [
        {
            value: 1,
            label: t('level_1'),
        },
        {
            value: 2,
            label: t('level_2'),
        },
        {
            value: 3,
            label: t('level_3'),
        },
    ], [t]);
    const [alarmLevelOption, setAlarmLevelOption] = useState<SelectItem<number>>(
        selected == null ? ALARM_LEVEL_LIST[0] : (
            ALARM_LEVEL_LIST.find(alarmLevelOption => alarmLevelOption.value === (selected.parameters as SirenControlParameters)?.alarm_level)
            || null),
    );
    const SIREN_LEVEL_LIST = useMemo<SelectItem<number>[]>(() => [
        {
            value: 0,
            label: t('all'),
        },
        {
            value: 1,
            label: t('level_1'),
        },
        {
            value: 2,
            label: t('level_2'),
        },
        {
            value: 3,
            label: t('level_3'),
        },
    ], [t]);
    const [sirenLevelOption, setSirenLevelOption] = useState<SelectItem<number>>(
        selected == null ? SIREN_LEVEL_LIST[0] : (
            SIREN_LEVEL_LIST.find(sirenLevelOption => sirenLevelOption.value === (selected.parameters as SirenControlParameters)?.alarm_level)
            || null),
    );
    const [alarmControlOption, setAlarmControlOption] = useState<SelectItem<ALARM_CONTROL_VALUE>>(
        selected == null ? null : (
            alarmControlOptionList.find(option => option.value.id === (selected?.parameters as AlarmControlParameters)?.relational_control_id)
        ) || null,
    );
    const SIREN_LIST = useMemo<SelectItem<number>[]>(() => [
        {
            value: 0,
            label: t('inactive'),
        },
        {
            value: 1,
            label: t('intermittent'),
        },
        {
            value: 2,
            label: t('continuous'),
        },
    ], [t]);
    const [alarmSirenOption, setAlarmSirenOption] = useState<SelectItem<number>>(
        selected == null ? SIREN_LIST[0] : (
            SIREN_LIST.find(sirenOption => sirenOption.value === (selected.parameters as AlarmControlParameters)?.siren_type)
            || null),
    );
    const HEATING_MODE_LIST = useMemo<SelectItem<number>[]>(() => [
        {
            value: 1,
            label: t('heating'),
        },
        {
            value: 2,
            label: t('cooling'),
        },
        {
            value: 3,
            label: t('heating_cooling'),
        },
    ], [t]);
    const [heatingModeOption, setHeatingModeOption] = useState<SelectItem<number>>(
        selected == null ? HEATING_MODE_LIST[0] : (
            HEATING_MODE_LIST.find(heatingOption => heatingOption.value === (
                selected.temperature_settings?.has_cooling && selected.temperature_settings?.has_heating ? 3 :
                    (selected.temperature_settings?.has_cooling ? 2 : 1)
            ))
            || null),
    );

    const FANCOIL_HEATING_MODE_LIST = useMemo<SelectItem<number>[]>(() => [
        {
            value: 1,
            label: t('heating_cooling'),
        },
    ], [t]);

    const [fancoilHeatingModeOption, setFancoilHeatingModeOption] = useState<SelectItem<number>>(
        selected == null ? FANCOIL_HEATING_MODE_LIST[0] : (
            FANCOIL_HEATING_MODE_LIST.find(heatingOption => heatingOption.value === (
                selected.temperature_settings?.has_cooling && selected.temperature_settings?.has_heating ? 3 :
                    (selected.temperature_settings?.has_cooling ? 2 : 1)
            ))
            || null),
    );

    const FANCOIL_FAN_TYPE_LIST = useMemo<SelectItem<number>[]>(
        () => [
            {
                value: 0,
                label: t("control_edit_page.temp_output_type_bulk")
            },
            {
                value: 1,
                label: t("control_edit_page.temp_output_type_sequential")
            }
        ],
        [t]
    )

    const [fancoilFanType, setFancoilFanType] = useState<SelectItem<number>>(
        selected == null
            ? null
            : FANCOIL_FAN_TYPE_LIST.find(
                  fanType => fanType.value === (selected.parameters as ControlFancoilSet)?.fan_type
              ) || null
    )

    const [fancoilOutputOption, setFancoilOutputOption] = useState<SelectItem<number>>(
        selected == null ? null :
                outputNumberOptionList.find(outputOption => outputOption.value === (selected.parameters as ControlFancoilSet)?.output_number)
                || null
    )
    const [fancoilFan1Option, setFancoilFan1Option] = useState<SelectItem<number>>(
        selected == null ? null :
            outputNumberOptionList.find(fanOption => fanOption.value === (selected.parameters as ControlFancoilSet)?.fan_output_low)
            || null,
    );

    const [fancoilFan2Option, setFancoilFan2Option] = useState<SelectItem<number>>(
        selected == null ? null :
            outputNumberOptionList.find(fanOption => fanOption.value === (selected.parameters as ControlFancoilSet)?.fan_output_medium)
            || null,
    );

    const [fancoilFan3Option, setFancoilFan3Option] = useState<SelectItem<number>>(
        selected == null ? null :
            outputNumberOptionList.find(fanOption => fanOption.value === (selected.parameters as ControlFancoilSet)?.fan_output_high)
            || null,
    );

    const [alarmDelaySeconds, setAlarmDelaySeconds] = useState(
        (selected?.parameters as AlarmControlParameters)?.delay_time?.toString()
        || '');
    const defaultValueOptionList = useMemo(() => {
        if (!controlType) {
            return [];
        }

        return CONTROL_TYPE_VALUE_MAP[controlType.value.render_type].map(value => ({...value, label: t(value.label)}));
    }, [t, controlType]);
    const [defaultValueOption, setDefaultValueOption] = useState<SelectItem<number>>(
        (selected == null ? null : defaultValueOptionList.find(defaultValueOption => defaultValueOption.value === (selected.parameters as IOControlParameters)?.default_value))
        || defaultValueOptionList[0] || null,
    );

    const onBackPressed = useCallback(() => {
        history.push(BACK_URL);
    }, [history]);

    const goToInputEditScreenWithState = useCallback((state: unknown) => {
        history.push({pathname: INPUT_EDIT_URL, state});
    }, [history]);

    const goToStpEditorWithState = useCallback((state: unknown) => {
        //     if (controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT) {
        //         state['controlId'] = controlList.find(c => c.isVirtual === true && state['controlId'] === c.control.id)?.id || state['controlId'];
        //     }
        // todo above not working, as a result thermostat virtualControl's stpUIEntity page cannot be redirected
        history.push({pathname: `/settings/stp-editor/${bridgeDevice.value.id}/content/`, state});
    }, [bridgeDevice, history/*, controlType, controlList*/]);


     const goToFrameEditorWithState = useCallback((state: unknown) => {
        history.push({pathname: `/settings/frame-editor/${bridgeDevice.value.id}/content/`, state});
    }, [bridgeDevice, history]);

    const onDeletePressed = useCallback(() => {
        if(!Boolean(selectedRemoteControl?.parameters?.['is_remote_control'])){
            return rpc.deleteControl(selected?.id)
            .then(() => {
                success(t('control_successfully_deleted'));
                onBackPressed();
            })
            .catch(failure);
        } else {
            return rpc.deleteVirtualControl(selectedRemoteControl.id).then(() => {
                success(t('control_successfully_deleted'));
                onBackPressed();
            })
            .catch(failure);
        }
    }, [t, failure, rpc, selectedRemoteControl, success, onBackPressed, selected?.id]);

    const isFieldsValid = useCallback((): boolean => {
        const controlTypeID = controlType?.value?.id;
        if(bridgeDevice.value.type_id !== BRIDGE_DEVICE_TYPE_ID_FRAME || !isRemoteControl){
        if (name == null || name.length === 0) {
            failure(t('pls_enter_control_name'));

            return false;
        }

        if (bridgeDevice?.value == null) {
            failure(t('pls_select_device'));

            return false;
        }

        if (controlType == null) {
            failure(t('pls_select_control_type'));

            return false;
        }

        if (
            controlTypeID !== CONTROL_TYPE_SCREEN_SAVER &&
            controlTypeID !== SOUND_PLAYED_TYPE_ID &&
            controlTypeID !== SOUND_LEVEL_TYPE_ID &&
            areaOption?.value == null
        ) {
            failure(t('pls_select_area'));
            return false;
        }
        console.log(
            'controlTypeID === CONTROL_TYPE_ID_SENSOR_ENERGY',
            controlTypeID === CONTROL_TYPE_ID_SENSOR_ENERGY
        )
        console.log(
            'alarmDisableValue === null',
            alarmDisableValue === null 
        )
        console.log({alarmDisableValue});
        if (
            controlTypeID === CONTROL_TYPE_ID_SENSOR_ENERGY &&
            !alarmDisableValue
        ) {
            failure(t('pls_select_alarm_disable_value'))
            return false;
        }
        if (
            controlTypeID === CONTROL_TYPE_ID_EXTERNAL_ALARM &&
            !alarmArmedValue
        ) {
            failure(t('pls_select_alarm_set_value'))
            return false;
        }

         /*if (controlType?.value?.id === CONTROL_TYPE_ID_FANCOIL) {
            if (fancoilHeatingModeOption?.value == null) {
                failure(t("pls_select_valve_output"))
                return false
            }
            if(fancoilFan1Option?.value == null){
                failure(t("pls_select_fan_1"))
                return false;
            }
            if (fancoilFan2Option?.value == null) {
                failure(t("pls_select_fan_2"))
                return false
            }
            if (fancoilFan3Option?.value == null) {
                failure(t("pls_select_fan_3"))
                return false
            }
        }*/

        //KNX VALIDATIONS

        if(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX){
            if(controlTypeID === CONTROL_TYPE_ID_LIGHT) {
                if(!isValidKNXValue(knxLampOutputAddress) || !isValidKNXValue(knxLampFeedbackAddress)) {
                    failure(t('pls_enter_a_valid_value'));
                    return false;
                }
            }

            if(controlTypeID === CONTROL_TYPE_ID_CURTAIN) {
                if(!isValidKNXValue(knxCurtainStopAddress) || !isValidKNXValue(knxCurtainUpDownAddress)) {
                    failure(t('pls_enter_a_valid_value'));
                    return false;
                }
            }

            if(controlTypeID === CONTROL_TYPE_ID_ANALOG) {
                if(!isValidKNXValue(knxDimmerFeedbackAddress) || !isValidKNXValue(knxDimmerOutputAddress)) {
                    failure(t('pls_enter_a_valid_value'));
                    return false;
                }
            }

            if(controlTypeID === CONTROL_TYPE_ID_PLUG) {
                if(!isValidKNXValue(knxPlugFeedbackAddress) || !isValidKNXValue(knxPlugOutputAddress)) {
                    failure(t('pls_enter_a_valid_value'));
                    return false;
                }
            }

            if(controlTypeID === CONTROL_TYPE_ID_THERMOSTAT) {
                if(heatingModeOption?.value === 1 || heatingModeOption?.value === 3) {
                    if(!isValidKNXValue(knxThermostatHeatingFeedbackAddress)){
                        failure(t('pls_enter_a_valid_value'));
                        return false;
                    }
                } else if(heatingModeOption?.value === 2 || heatingModeOption?.value === 3) {
                    if(!isValidKNXValue(knxThermostatCoolingFeedbackAddress)){
                        failure(t('pls_enter_a_valid_value'));
                        return false;
                    }
                }
                if(
                    !isValidKNXValue(knxThermostatCurrentSetpointFeedbackAddress) ||
                    !isValidKNXValue(knxThermostatSetpointOutputAddress) ||
                    !isValidKNXValue(knxThermostatModeFeedbackAddress) ||
                    !isValidKNXValue(knxThermostatModeOutputAddress) ||
                    !isValidKNXValue(knxThermostatCurrentTempFeedbackAddress)) {
                    failure(t('pls_enter_a_valid_value'));
                    return false;
                }
            }

            if(controlTypeID === CONTROL_TYPE_ID_FANCOIL) {
                // TODO: Use Array.some here.
                if(
                    !isValidKNXValue(fancoilStatusOutput) ||
                    !isValidKNXValue(fancoilCurrentTempFeedback) ||
                    !isValidKNXValue(fancoilStatusFeedback) ||
                    !isValidKNXValue(fancoilModeOutput) ||
                    !isValidKNXValue(fancoilModeFeedback) ||
                    !isValidKNXValue(fancoilHeatingModeFeedback) ||
                    !isValidKNXValue(fancoilFanSpeedOutput) ||
                    !isValidKNXValue(fancoilFanSpeedFeedback) ||
                    !isValidKNXValue(fancoilAutoFanOutput) ||
                    !isValidKNXValue(fancoilCoolingModeFeedback) ||
                    !isValidKNXValue(fancoilCurrentSetpointFeedback) ||
                    !isValidKNXValue(fancoilAutoFanFeedback) ||
                    !isValidKNXValue(fancoilCurrentSetpointOutput)) {
                    failure(t('pls_enter_a_valid_value'));
                    return false;
                }
            }
        }

        //KNX VALIDATIONS

        if (
            controlTypeID !== CONTROL_TYPE_SCREEN_SAVER &&
            controlTypeID !== SOUND_PLAYED_TYPE_ID &&
            controlTypeID !== SOUND_LEVEL_TYPE_ID &&
            notificationReverse
        ) {
            if (pickedTimeStringBeginning === '') {
                failure(t("control_edit_page.please_select_a_start_time"))
                return false
            }
            if (pickedTimeStringFinish === '') {
                failure(t("control_edit_page.please_select_a_time_time"))
                return false
            }
            const timeBeginingHour = pickedTimeStringBeginning[0] + pickedTimeStringBeginning[1]
            const timeBeginingMinute = pickedTimeStringBeginning[3] + pickedTimeStringBeginning[4]
            const timeFinishHour = pickedTimeStringFinish[0] + pickedTimeStringFinish[1]
            const timeFinishMinute = pickedTimeStringFinish[3] + pickedTimeStringFinish[4]

            if (timeBeginingHour > timeFinishHour) {
                failure(t("control_edit_page.the_start_time_must_be_smaller_than_the_end_time"))
                return false
            }
            if (timeFinishHour <= timeBeginingHour) {
                if (timeBeginingMinute >= timeFinishMinute) {
                    failure(t("control_edit_page.the_start_minute_must_be_less_than_the_end_minute"))
                    return false
                }
            }
        }

        //Boş-Dolu Sorgusu

        if (controlTypeID === CONTROL_TYPE_ID_ALARM) {
            if (alarmControlOption?.value == null) {
                failure(t('pls_select_control'));
                return false;
            }

            if (alarmSirenOption?.value == null) {
                failure(t('pls_select_siren'));
                return false;
            }

            if (alarmLevelOption?.value == null) {
                failure(t('pls_select_alarm_level'));
                return false;
            }

            if (!isValidDuration(alarmDelaySeconds)) {
                failure(t('pls_enter_a_valid_delay_duration'));
                return false;
            }
        } else if (controlTypeID === CONTROL_TYPE_ID_THREE_ANALOG) {
            if (outputNumber?.value == null) {
                failure(t("pls_enter_output_no"));
                return false;
            }
            if (rgbListOptionRed?.value == null) {
                failure(t("control_edit_page.please_enter_the_red_field"))
                return false;
            }
            if (rgbListOptionGreen?.value == null) {
                failure(t("control_edit_page.please_enter_the_green_field"))
                return false;
            }
            if (rgbListOptionBlue?.value == null) {
                failure(t("control_edit_page.please_enter_the_blue_field"))
                return false;
            }
        } else if (controlTypeID === CONTROL_TYPE_ID_SIREN) {
            if (outputNumber?.value == null) {
                failure(t('pls_enter_output_no'));
                return false;
            }

            if (sirenLevelOption?.value == null) {
                failure(t('pls_select_alarm_level'));
                return false;
            }
        } else if (controlTypeID === CONTROL_TYPE_ID_THERMOSTAT) {
            if (isBridgeDeviceTypeIO && thermostatBridgeDevice?.value == null) {
                failure(t('pls_select_sensor_device'));
                return false;
            }

            if (!isBridgeDeviceTypeIO) {
                const foundControl = controlList.find(c => c.isVirtual === false &&
                    c.control.id !== selected?.id &&
                    c.control.temperature_settings?.bridge_device_id === bridgeDevice.value.id,
                );
                if (foundControl != null && bridgeDevice?.value?.type_id !== BRIDGE_DEVICE_TYPE_ID_STS_PLUS && bridgeDevice?.value?.type_id !== BRIDGE_DEVICE_TYPE_ID_STP && bridgeDevice?.value?.type_id !== BRIDGE_DEVICE_TYPE_ID_STPM_F) {
                    failure(t('thermostat_was_previously_added_to_device', {
                        deviceName: bridgeDevice.value.name,
                        controlName: foundControl.control.name,
                    }));

                    return false;
                }

                /*const foundControlSTSP = controlList.find(c => c.isVirtual === false &&
                    c.control.id !== selected?.id &&
                    c.control.temperature_settings?.bridge_device_id === bridgeDevice.value.id &&
                    c.control?.type_id === (CONTROL_TYPE_ID_THERMOSTAT || CONTROL_TYPE_ADV_THERMOSTAT || CONTROL_TYPE_ID_FANCOIL) && [c].length <= 2
                );
                if (foundControlSTSP != null && bridgeDevice?.value?.type_id === BRIDGE_DEVICE_TYPE_ID_STS_PLUS) {
                    failure(t('thermostat_was_previously_added_to_device', {
                        deviceName: bridgeDevice.value.name,
                        controlName: foundControlSTSP.control.name,
                    }));
                    return false;
                }*/

            }

            if (!isBridgeDeviceTypeIO) {
                if (heatingModeOption?.value === 2 || heatingModeOption?.value === 3) {
                    if (downOutputNumber?.value == null && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX) {
                        failure(t('pls_select_cold_exit_no'));

                        return false;
                    }
                }

                if (heatingModeOption?.value === 1 || heatingModeOption?.value === 3) {
                    if (outputNumber?.value == null && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX) {
                        failure(t('pls_select_hot_exit_no'));

                        return false;
                    }
                }
            } else {
                if (outputNumber?.value == null) {
                    failure(t('pls_select_exit_no'));
                    return false;
                }
            }
        } else {
            if (
                controlTypeID !== CONTROL_TYPE_SCREEN_SAVER &&
                controlTypeID !== SOUND_PLAYED_TYPE_ID &&
                controlTypeID !== SOUND_LEVEL_TYPE_ID &&
                controlTypeID !== CONTROL_TYPE_ID_FANCOIL &&
                isBridgeDeviceTypeIO
            ) {
                switch (controlType.value.render_type) {
                    case 'simple':
                        if (outputNumber?.value == null) {
                            failure(t('pls_select_exit_no'));
                            return false;
                        }
                        if (
                            !rememberLastValue && 
                            defaultValueOption?.value == null &&
                            bridgeDevice?.value?.type_id !== DeviceTypes.ETHERNET_IO &&
                            ![
                                CONTROL_TYPE_ADV_THERMOSTAT,
                                CONTROL_TYPE_ID_EXTERNAL_ALARM,
                                CONTROL_TYPE_ID_SENSOR_ENERGY
                            ].includes(controlTypeID)
                        ) {                        
                            failure(t('pls_select_default_value'));
                            return false;
                        }
                        break;
                    case 'two_phase':
                    case 'three_phase':
                        if (outputNumber?.value == null) {
                            failure(t('pls_select_up_exit_no'));
                            return false;
                        }

                        if (downOutputNumber?.value == null) {
                            failure(t('pls_select_down_exit_no'));
                            return false;
                        }

                        if (outputNumber.value !== NO_OUTPUT_OPTION.value &&
                            outputNumber.value === downOutputNumber.value) {
                            failure(t('up_down_exit_no_cannot_be_same'));
                            return false;
                        }

                        if (controlTypeID !== CONTROL_TYPE_ID_MKK && !isValidDuration(outputDurationLimitSeconds)) {
                            failure(t('pls_enter_a_valid_exit_duration_limit'));
                            return false;
                        }

                        if (controlTypeID === CONTROL_TYPE_ID_MKK &&
                            !isValidDurationForMDL(outputDurationLimitSeconds)) {
                            failure(t('pls_enter_a_valid_exit_duration_limit'));
                            return false;
                        }

                        if (
                            !rememberLastValue &&
                            defaultValueOption?.value == null &&
                            bridgeDevice?.value?.type_id !== DeviceTypes.ETHERNET_IO &&
                            ![
                                CONTROL_TYPE_ADV_THERMOSTAT,
                                CONTROL_TYPE_ID_MKK,
                                CONTROL_TYPE_ID_SENSOR_ENERGY,
                                CONTROL_TYPE_ID_EXTERNAL_ALARM
                            ].includes(controlTypeID)
                        ) {
                            failure(t('pls_select_default_value'));
                            return false;
                        }

                        break;
                    case 'pulse':
                        if (outputNumber?.value == null) {
                            failure(t('pls_select_up_exit_no'));
                            return false;
                        }

                        if (!isValidPulseDuration(pulseDuration)) {
                            failure(t('pls_enter_a_valid_pulse_duration'));
                            return false;
                        }

                        if (controlType?.value?.render_type !== 'pulse' && !rememberLastValue && defaultValueOption?.value == null && bridgeDevice?.value?.type_id !== DeviceTypes.ETHERNET_IO && controlType?.value?.id !== CONTROL_TYPE_ADV_THERMOSTAT && controlType?.value?.id !== CONTROL_TYPE_ID_MKK) {
                            failure(t('pls_select_default_value'));
                            return false;
                        }

                        break;
                    case 'complex':
                        if (!rememberLastValue && defaultValueOption?.value == null && bridgeDevice?.value?.type_id !== DeviceTypes.ETHERNET_IO && controlType?.value?.id !== CONTROL_TYPE_ADV_THERMOSTAT && controlType?.value?.id !== CONTROL_TYPE_ID_VRF && controlType?.value?.id !== CONTROL_TYPE_GENERAL_TEMPERATURE && controlType?.value?.id !== CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN && controlType?.value?.id !== CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON) {

                            failure(t('pls_select_default_value'));

                            return false;
                        }
                        break;
                    default:
                        break;
                }
            } else {
                if (bridgeDeviceType?.button_count > 0) {
                    if (buttonNumber?.value == null && controlTypeID !== CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON) {
                        failure(t('pls_select_button_number'));

                        return false;
                    }
                }

                switch (controlType.value.render_type) {
                    case 'simple':
                        if (outputNumber?.value == null && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX) {
                            failure(t('pls_select_exit_no'));

                            return false;
                        }

                        break;
                    case 'two_phase':
                    case 'three_phase':
                        if (doubledOutputNumber?.value == null && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX) {
                            failure(t('pls_select_exit_no'));

                            return false;
                        }

                        if (!isValidDuration(outputDurationLimitSeconds) && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX) {
                            failure(t('pls_enter_a_valid_exit_duration_limit'));

                            return false;
                        }

                        break;
                    case 'pulse':
                        // no validations
                        break;
                    case 'complex':
                        if ((controlType.value.id === CONTROL_TYPE_ID_DIMMER || controlType.value.id === CONTROL_TYPE_ID_ANALOG) && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX) {
                            if (outputNumber?.value == null) {
                                failure(t('pls_select_exit_no'));

                                return false;
                            }

                            if (!isValidMaxDimmerValue(maxValue)) {
                                failure(t('pls_enter_a_valid_maximum_value'));

                                return false;
                            }

                            if (!isValidMinDimmerValue(minValue)) {
                                failure(t('pls_enter_a_valid_minimum_value'));

                                return false;
                            }

                            if (Number(maxValue) <= Number(minValue)) {
                                failure(t('maximum_value_must_be_greater_then_minimum_vale'));

                                return false;
                            }
                        }
                        if(controlType?.value?.id === SOUND_PLAYED_TYPE_ID){
                            if(soundControl?.value == null){
                                failure(t('pls_enter_name'));

                                return false;
                            }
                        }
                        if(controlTypeID === CONTROL_TYPE_ID_FANCOIL && bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_CCM){
                            if(fancoilFanType?.value == null){
                                failure(t("control_edit_page.please_select_fan_type"))
                                return false;
                            }
                            if (heatingModeOption?.value === 3) {
                                if (outputNumber?.value === downOutputNumber?.value) {
                                    failure(t("control_edit_page.heating_and_cooling_outputs_cannot_be_the_same"))
                                    return false
                                }
                            }
                        }
                        if(controlTypeID === CONTROL_TYPE_SCREEN_SAVER){
                            if(screenSaver?.value == null){
                                failure(t('pls_enter_name'));
                                return false;
                            }
                        }
                        break;
                    default:
                        break;
                }
            }
        }
        } else{
            if (name == null || name.length === 0) {
                failure(t('pls_enter_control_name'));

                return false;
            }

            if (bridgeDevice?.value == null) {
                failure(t('pls_select_device'));

                return false;
            }
        }


        return true;
    }, [
        t,
        failure,
        name,
        bridgeDevice,
        isRemoteControl,
        bridgeDeviceType,
        areaOption,
        isBridgeDeviceTypeIO,
        controlType,
        outputNumber?.value,
        rememberLastValue,
        defaultValueOption,
        buttonNumber?.value,
        outputDurationLimitSeconds,
        downOutputNumber?.value,
        doubledOutputNumber?.value,
        pulseDuration,
        maxValue,
        minValue,
        alarmLevelOption?.value,
        alarmControlOption?.value,
        alarmSirenOption?.value,
        alarmDelaySeconds,
        thermostatBridgeDevice?.value,
        heatingModeOption?.value,
        sirenLevelOption?.value,
        soundControl?.value,
        screenSaver?.value,
        controlList,
        selected?.id,
        notificationReverse,
        pickedTimeStringBeginning,
        pickedTimeStringFinish,
        rgbListOptionRed?.value,
        rgbListOptionBlue?.value,
        rgbListOptionGreen?.value,
        fancoilFanType?.value,
        knxCurtainStopAddress,
        knxCurtainUpDownAddress,
        knxDimmerFeedbackAddress,
        knxDimmerOutputAddress,
        knxLampFeedbackAddress,
        knxLampOutputAddress,
        knxPlugFeedbackAddress,
        knxPlugOutputAddress,
        knxThermostatCoolingFeedbackAddress,
        knxThermostatCurrentSetpointFeedbackAddress,
        knxThermostatCurrentTempFeedbackAddress,
        knxThermostatHeatingFeedbackAddress,
        knxThermostatModeFeedbackAddress,
        knxThermostatModeOutputAddress,
        knxThermostatSetpointOutputAddress,
        fancoilAutoFanFeedback,
        fancoilAutoFanOutput,
        fancoilCoolingModeFeedback,
        fancoilCurrentSetpointFeedback,
        fancoilCurrentSetpointOutput,
        fancoilFanSpeedFeedback,
        fancoilFanSpeedOutput,
        fancoilHeatingModeFeedback,
        fancoilModeFeedback,
        fancoilModeOutput,
        fancoilStatusFeedback,
        fancoilStatusOutput,
        fancoilCurrentTempFeedback
    ]);

    const passwordControlQuery = useMemo(() => {
        if(controlType?.value?.id !== CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON &&
            controlType?.value?.id !== CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN &&
            controlType?.value?.id !== CONTROL_TYPE_GENERAL_TEMPERATURE &&
            controlType?.value?.id !== CONTROL_TYPE_SCREEN_SAVER &&
            controlType?.value?.id !== CONTROL_TYPE_ID_SIREN &&
            controlType?.value?.id !== CONTROL_TYPE_ID_ALARM &&
            controlType?.value?.id !== SOUND_PLAYED_TYPE_ID &&
            controlType?.value?.id !== SOUND_LEVEL_TYPE_ID &&
            controlType?.value?.id !== CONTROL_TYPE_ID_ENERGY &&
            controlType?.value?.id !== CONTROL_TYPE_ID_CAMERA_ENERGY_RELAY &&
            controlType?.value?.id !== CONTROL_TYPE_ID_BELL) {
                return true
        }
        return false
    }, [controlType])

    const onCreatePressed = useCallback(() => {
        if (!isFieldsValid()) {
            return;
        }

        let ioOrGlassParameters: DeviceParameters;
        if (isBridgeDeviceTypeIO) {
            ioOrGlassParameters = {
                should_output_reverse: outputReverse,
                should_remember_last_value: controlType?.value?.id === CONTROL_TYPE_ID_MKK ? false : rememberLastValue,
                default_value: controlType?.value?.id === CONTROL_TYPE_ID_MKK ? 0 :  defaultValueOption?.value ? defaultValueOption?.value : 0,
                output_number: outputNumber?.value,
            };
        } else {
            ioOrGlassParameters = {
                output_number: outputNumber?.value,
                button_number: controlType?.value?.id === CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON ? 0 : buttonNumber?.value
            }
        }

        let controlParameters: ControlParameters = null;
        let temperatureSettings: TemperatureControlSettings = null;
        switch (controlType?.value?.render_type) {
            case 'simple':
                controlParameters = {
                    should_output_reverse: outputReverse,
                    is_notification: notificationReverse,
                    start_time: pickedTimeStringBeginning,
                    end_time: pickedTimeStringFinish
                };

                if (
                    [
                        CONTROL_TYPE_ID_SENSOR_ENERGY,
                        CONTROL_TYPE_ID_EXTERNAL_ALARM
                    ].includes(controlType?.value?.id)
                ) {
                    ioOrGlassParameters = {
                        ...ioOrGlassParameters,
                        should_remember_last_value: true
                    }
                }
                if (controlType?.value?.id === CONTROL_TYPE_ID_SENSOR_ENERGY) {
                    controlParameters = {
                        ...controlParameters,
                        alarm_disable_value: Number(alarmDisableValue?.value),
                    }
                }
                if (controlType?.value?.id === CONTROL_TYPE_ID_EXTERNAL_ALARM) {
                    controlParameters = {
                        ...controlParameters,
                        alarm_set_value: Number(alarmArmedValue?.value)
                    };
                }

                if(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX) {
                    if(controlType.value.id === CONTROL_TYPE_ID_PLUG) {
                        controlParameters = {
                            ...controlParameters,
                            plug_feedback_address: knxPlugFeedbackAddress,
                            plug_output_address: knxPlugOutputAddress
                        }
                    }
                    if(controlType.value.id === CONTROL_TYPE_ID_LIGHT) {
                        controlParameters = {
                            ...controlParameters,
                            lamp_feedback_address: knxLampFeedbackAddress,
                            lamp_output_address: knxLampOutputAddress
                        }
                    }

                }
                break;
            case 'two_phase':
            case 'three_phase':
                controlParameters = {
                    should_output_reverse: outputReverse,
                    down_output_number: isBridgeDeviceTypeIO ? downOutputNumber?.value :
                        doubledOutputNumber?.value === NO_OUTPUT_OPTION?.value ? NO_OUTPUT_OPTION?.value :
                            doubledOutputNumber?.value + 1,
                    output_time_limit: Number(outputDurationLimitSeconds),
                };
                if (!isBridgeDeviceTypeIO) {
                    ioOrGlassParameters.output_number = doubledOutputNumber?.value;
                }

                if(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX) {
                    controlParameters = {
                        should_output_reverse: false, //maybe to do
                        down_output_number: 0,        //maybe to do
                        output_time_limit: 0,         //maybe to do
                        blinds_stop_address: knxCurtainStopAddress,
                        blinds_up_down_address: knxCurtainUpDownAddress
                    }
                }

                break;
            case 'pulse':
                controlParameters = {
                    pulse_time: Number(pulseDuration) * 10,
                    is_notification: notificationReverse,
                    start_time: pickedTimeStringBeginning,
                    end_time: pickedTimeStringFinish
                };
                break;
            case 'complex':
                switch (controlType.value.id) {
                    case CONTROL_TYPE_ID_ANALOG:
                        controlParameters = {
                            min_limit: Number(minValue),
                            max_limit: Number(maxValue),
                            is_notification: notificationReverse,
                            start_time: pickedTimeStringBeginning,
                            end_time: pickedTimeStringFinish
                        };
                        if(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX) {
                            controlParameters = {
                                ...controlParameters,
                                dimmer_feedback_address: knxDimmerFeedbackAddress,
                                dimmer_output_address: knxDimmerOutputAddress
                            }
                        }
                        break;
                    case CONTROL_TYPE_ID_THREE_ANALOG:
                        controlParameters = {
                            map_r: rgbListOptionRed?.value,
                            map_g: rgbListOptionGreen?.value,
                            map_b: rgbListOptionBlue?.value
                        }
                        break;
                    case CONTROL_TYPE_ID_DIMMER:
                        controlParameters = {
                            min_limit: Number(minValue),
                            max_limit: Number(maxValue),
                            is_notification: notificationReverse,
                            start_time: pickedTimeStringBeginning,
                            end_time: pickedTimeStringFinish
                        };
                        break;
                    case CONTROL_TYPE_ID_ALARM:
                        controlParameters = {
                            is_virtual_control: alarmControlOption.value.isVirtual,
                            relational_control_id: alarmControlOption.value.id,
                            siren_type: alarmSirenOption.value,
                            alarm_level: alarmLevelOption.value,
                            delay_time: Number(alarmDelaySeconds),
                            is_notification: notificationReverse,
                            start_time: pickedTimeStringBeginning,
                            end_time: pickedTimeStringFinish
                        };
                        break;
                    case CONTROL_TYPE_ID_SIREN:
                        controlParameters = {
                            alarm_level: sirenLevelOption.value,
                            is_notification: notificationReverse,
                            start_time: pickedTimeStringBeginning,
                            end_time: pickedTimeStringFinish
                        };
                        break;
                    case CONTROL_TYPE_ID_THERMOSTAT:
                        temperatureSettings = {
                            bridge_device_id: isBridgeDeviceTypeIO ? thermostatBridgeDevice.value.id : bridgeDevice.value.id,
                            is_mode_heating: heatingModeOption.value === 1 || heatingModeOption.value === 3,
                            has_heating: heatingModeOption.value === 1 || heatingModeOption.value === 3,
                            has_cooling: heatingModeOption.value === 2 || heatingModeOption.value === 3,
                        };

                        if (temperatureSettings.has_cooling/*&& !(isBridgeDeviceTypeIO && isThermostatBridgeDeviceIO)*/) {
                            controlParameters = {
                                ...controlParameters,
                                down_output_number: downOutputNumber?.value,
                            };
                            if (!temperatureSettings.has_heating) {
                                ioOrGlassParameters = {
                                    ...ioOrGlassParameters,
                                    output_number: downOutputNumber?.value,
                                };
                            }
                        }

                        if(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX){
                            controlParameters = {
                                ...controlParameters,
                                thermostat_cooling_mode_feedback_address: knxThermostatCoolingFeedbackAddress,
                                thermostat_heating_mode_feedback_address: knxThermostatHeatingFeedbackAddress,
                                thermostat_current_setpoint_feedback_address: knxThermostatCurrentSetpointFeedbackAddress,
                                thermostat_current_temp_feedback_address: knxThermostatCurrentTempFeedbackAddress,
                                thermostat_mode_feedback_address: knxThermostatModeFeedbackAddress,
                                thermostat_mode_output_address: knxThermostatModeOutputAddress,
                                thermostat_setpoint_output_address: knxThermostatSetpointOutputAddress,
                            }
                        }

                        break;
                    case CONTROL_TYPE_ADV_THERMOSTAT:
                        temperatureSettings = {
                            bridge_device_id: isBridgeDeviceTypeIO ? thermostatBridgeDevice?.value?.id : bridgeDevice?.value?.id,
                            is_mode_heating: heatingModeOption.value === 1 || heatingModeOption.value === 3,
                            has_heating: heatingModeOption.value === 1 || heatingModeOption.value === 3,
                            has_cooling: heatingModeOption.value === 2 || heatingModeOption.value === 3,
                        };

                        if (temperatureSettings.has_cooling) {
                            controlParameters = {
                                ...controlParameters,
                                down_output_number: downOutputNumber.value,
                            };
                            if (!temperatureSettings.has_heating) {
                                ioOrGlassParameters = {
                                    ...ioOrGlassParameters,
                                    output_number: downOutputNumber.value,
                                };
                            }
                        }
                        break;
                    case SOUND_PLAYED_TYPE_ID:
                        controlParameters = {
                            sound_type: soundControl?.value
                        }
                        break;
                    case CONTROL_TYPE_SCREEN_SAVER:
                        controlParameters = {
                            screen_type: screenSaver?.value
                        }
                        break;
                    case CONTROL_TYPE_ID_VRF:
                        controlParameters = {
                            unit_id: Number(unitID) || 0
                        }
                        break
                    case CONTROL_TYPE_ID_FANCOIL:
                        controlParameters = {
                            output_number: isBridgeDeviceTypeIO ?  outputNumber?.value : fancoilOutputOption?.value,
                            fan_output_low: fancoilFan1Option?.value,
                            fan_output_medium: fancoilFan2Option?.value,
                            fan_output_high: fancoilFan3Option?.value
                        }

                        if(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX){
                            controlParameters = {
                                ...controlParameters,
                                control_mode_status: false,
                                control_power_status: false,
                                control_running_status: false,
                                fan_status: "low",
                                fancoil_heating_mode_fan_feedback_address: fancoilHeatingModeFeedback,
                                fancoil_cooling_mode_fan_feedback_address: fancoilCoolingModeFeedback,
                                fancoil_current_setpoint_output_address: fancoilCurrentSetpointOutput,
                                fancoil_current_setpoint_feedback_address: fancoilCurrentSetpointFeedback,
                                fancoil_current_temp_feedback_address: fancoilCurrentTempFeedback,
                                fancoil_fan_auto_fan_feedback_address: fancoilAutoFanFeedback,
                                fancoil_fan_auto_fan_output_address: fancoilAutoFanOutput,
                                fancoil_fan_speed_feedback_address: fancoilFanSpeedFeedback,
                                fancoil_fan_speed_output_address: fancoilFanSpeedOutput,
                                fancoil_status_feedback_address: fancoilStatusFeedback,
                                fancoil_status_output_address: fancoilStatusOutput,
                                fancoil_mode_feedback_address: fancoilModeFeedback,
                                fancoil_mode_output_address: fancoilModeOutput,
                            }
                        }

                        if (bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_CCM) {
                            controlParameters = {
                                ...controlParameters,
                                fan_type: fancoilFanType?.value,
                            }
                        }

                        if (isBridgeDeviceTypeIO) {
                            temperatureSettings = {
                                bridge_device_id: isBridgeDeviceTypeIO ? thermostatBridgeDevice.value.id : bridgeDevice.value.id,
                                is_mode_heating: heatingModeOption?.value === 1 || heatingModeOption?.value === 3,
                                has_heating: heatingModeOption?.value === 1 || heatingModeOption?.value === 3,
                                has_cooling: heatingModeOption?.value === 2 || heatingModeOption?.value === 3
                            }
                        }
                        else{
                            temperatureSettings = {
                                bridge_device_id: isBridgeDeviceTypeIO ? thermostatBridgeDevice.value.id : bridgeDevice.value.id,
                                is_mode_heating: fancoilHeatingModeOption?.value === 1 || fancoilHeatingModeOption?.value === 3
                            }
                        }

                        if (temperatureSettings.has_cooling && isBridgeDeviceTypeIO) {
                            controlParameters = {
                                ...controlParameters,
                                down_output_number: downOutputNumber?.value
                            }
                            if (!temperatureSettings.has_heating) {
                                ioOrGlassParameters = {
                                    ...ioOrGlassParameters,
                                    output_number: downOutputNumber?.value
                                }
                            }
                        }
                        break;
                   case CONTROL_TYPE_GENERAL_TEMPERATURE:
                        ioOrGlassParameters = {
                            ...ioOrGlassParameters,
                            output_number: downOutputNumber?.value || 99
                        }
                       temperatureSettings = {
                            bridge_device_id: bridgeDevice?.value?.id,
                            is_mode_heating: true,
                            has_heating: false,
                            has_cooling: false
                        }
                        break;
                    default:
                       // failure(t('undefined_control_type'));

                        break;
                }

                break;
            default:
                break;
        }

        const controlModel: ControlModel = {
            ...(selected ? selected : {}),
            id: selectedRemoteControl?.parameters?.['is_remote_control'] ? selectedRemoteControl?.id : (selected?.id || null),
            name,
            area_id: areaOption?.value.id,
            bridge_device_id: bridgeDevice.value.id,
            type_id: controlType?.value?.id,
            parameters: {...ioOrGlassParameters, ...controlParameters, hide_control: hideControl, is_remote_control: isRemoteControl, password_control: passwordControlQuery === false ? false : passwordControl, order: selected?.parameters?.['order']},
            temperature_settings: temperatureSettings,
        };

        const remoteControlModel: ControlModel = {
            ...(selectedRemoteControl ? selectedRemoteControl : {}),
            id: selectedRemoteControl?.parameters?.['is_remote_control'] ? (selectedRemoteControl?.id  || null) : selected?.id,
            name,
            bridge_device_id: bridgeDevice.value.id,
            area_id: null,
            type_id: null,
            parameters: {is_remote_control: isRemoteControl, relational_control_id: control?.value?.id} as any,
            temperature_settings: null,
        }

        const saveControl = (bridgeDevice?.value?.type_id === BRIDGE_DEVICE_TYPE_ID_FRAME && isRemoteControl) ? remoteControlModel : controlModel

        return rpc.updateControl(saveControl)
            .then((id: string) => {
                if (navigateToSTPEditor) {
                    goToStpEditorWithState({
                        controlId: id,
                        bridgeDeviceId: bridgeDevice.value.id,
                    });
                } else if(navigateToFrameEditor){
                    goToFrameEditorWithState({
                        controlId: id,
                        bridgeDeviceId: bridgeDevice.value.id,
                    });
                }else if (addInput) {
                    goToInputEditScreenWithState({
                        controlId: id,
                        bridgeDeviceId: bridgeDevice.value.id,
                        inputTypeId: controlType.value.id === CONTROL_TYPE_ID_ALARM ? INPUT_TYPE_ID_SENSOR : INPUT_TYPE_ID_MOMENTARY,
                    });
                } else {
                    success(t(selected?.id == null ? 'control_successfully_saved' : 'control_successfully_edited'));
                    onBackPressed();
                }
            })
            .catch(failure);
    }, [
        t,
        isFieldsValid,
        success,
        failure,
        onBackPressed,
        goToInputEditScreenWithState,
        goToFrameEditorWithState,
        rpc,
        selectedRemoteControl,
        isRemoteControl,
        addInput,
        selected,
        name,
        control,
        bridgeDevice,
        areaOption,
        navigateToFrameEditor,
        isBridgeDeviceTypeIO,
        controlType,
        rememberLastValue,
        defaultValueOption,
        outputReverse,
        outputNumber?.value,
        buttonNumber?.value,
        downOutputNumber?.value,
        outputDurationLimitSeconds,
        doubledOutputNumber?.value,
        pulseDuration,
        maxValue,
        passwordControlQuery,
        minValue,
        sirenLevelOption?.value,
        alarmLevelOption?.value,
        alarmSirenOption?.value,
        alarmDelaySeconds,
        alarmControlOption?.value,
        thermostatBridgeDevice?.value?.id,
        heatingModeOption?.value,
        navigateToSTPEditor,
        soundControl?.value,
        screenSaver?.value,
        rgbListOptionRed?.value,
        rgbListOptionGreen?.value,
        rgbListOptionBlue?.value,
        goToStpEditorWithState,
        fancoilHeatingModeOption,
        fancoilOutputOption,
        fancoilFan1Option,
        fancoilFan2Option,
        fancoilFan3Option,
        notificationReverse,
        pickedTimeStringBeginning,
        pickedTimeStringFinish,
        fancoilFanType?.value,
        bridgeDeviceType?.id,
        unitID,
        knxCurtainStopAddress,
        knxCurtainUpDownAddress,
        knxDimmerFeedbackAddress,
        knxDimmerOutputAddress,
        knxLampFeedbackAddress,
        knxPlugFeedbackAddress,
        knxPlugOutputAddress,
        knxThermostatCoolingFeedbackAddress,
        knxThermostatHeatingFeedbackAddress,
        knxThermostatCurrentSetpointFeedbackAddress,
        knxThermostatCurrentTempFeedbackAddress,
        knxThermostatModeFeedbackAddress,
        knxThermostatModeOutputAddress,
        knxThermostatSetpointOutputAddress,
        knxLampOutputAddress,
        passwordControl,
        fancoilAutoFanFeedback,
        fancoilAutoFanOutput,
        fancoilCoolingModeFeedback,
        fancoilCurrentSetpointFeedback,
        fancoilCurrentSetpointOutput,
        fancoilFanSpeedFeedback,
        fancoilFanSpeedOutput,
        fancoilHeatingModeFeedback,
        fancoilModeFeedback,
        fancoilModeOutput,
        fancoilStatusFeedback,
        fancoilStatusOutput,
        fancoilCurrentTempFeedback,
        hideControl,
        alarmArmedValue,
        alarmDisableValue
    ])

    useEffect(() => {
        if(isBusy) {
            timeout = setTimeout(() => {
                dispatch(changeBusyAction(false))
            }, 10000)
            return () => clearTimeout(timeout)
        }
    }, [dispatch, isBusy])

    const setOutputReverseCheckbox = useCallback(() => {
        setOutputReverse(!outputReverse);
    }, [outputReverse]);
    const setShowControlCheckbox = useCallback(
        () => setHideControl(!hideControl), [hideControl]
    );
    const setPasswordControlCheckbox = useCallback(
        () => setPasswordControl(!passwordControl), [passwordControl]
    );
    const setNotificationReverseCheckbox = useCallback(()=> {
        setNotificationReverse(!notificationReverse)
    },[notificationReverse])

    const setRememberLastValueCheckbox = useCallback(() => {
        // When control type is sensor energy, this value will be locked.
        if (controlType?.value?.id === CONTROL_TYPE_ID_SENSOR_ENERGY) return;
        setRememberLastValue(rememberLastValue => !rememberLastValue);
    }, []);

    const setAddInputCheckbox = useCallback(() => {
        setAddInput(addInput => !addInput);
    }, []);

    const setNavigateStpEditorCheckbox = useCallback(() => {
        setNavigateToSTPEditor(!navigateToSTPEditor);
    }, [navigateToSTPEditor]);

     const setNavigateFrameEditorCheckbox = useCallback(() => {
        setNavigateToFrameEditor(!navigateToFrameEditor);
    }, [navigateToFrameEditor]);

    const buttons = useMemo(() => [
        {
            component: params?.id == null ? null :
                <SettingsDeleteButton
                    id={!Boolean(selectedRemoteControl?.parameters?.['is_remote_control']) ? selected?.id : selectedRemoteControl?.id}
                    type={!Boolean(selectedRemoteControl?.parameters?.['is_remote_control']) ? 'control' : "virtualControl"}
                    questionText={t('are_you_sure_you_want_to_delete_control')}
                    questionTextRelational={message => t('this_will_be_deleted_with_this_message__continue', {
                        name: !Boolean(selectedRemoteControl?.parameters?.['is_remote_control']) ? selected?.name : selectedRemoteControl?.parameters?.['remote_control_name'],
                        message,
                    })}
                    onPress={onDeletePressed}/>,
        },
        {
            text: t('save'),
            className: 'green',
            onClick: onCreatePressed,
        },
        {
            text: t('cancel'),
            className: 'red',
            url: BACK_URL,
        },
    ], [t, onCreatePressed, onDeletePressed, selectedRemoteControl, selected, params?.id]);

    const onBlur = useCallback(() => {
        setActiveInput(null);
        setActiveNumpadInput(null);
        setActiveNumpadExtendedInput(null);
    }, [setActiveInput, setActiveNumpadExtendedInput, setActiveNumpadInput]);

    useEffect(() => {
        setControlType(controlTypeOptionList.find(option => option.value === controlType?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [controlTypeOptionList]);

    useEffect(() => {
        setDefaultValueOption(defaultValueOptionList.find(option => option.value === defaultValueOption?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValueOptionList]);

    useEffect(()=> {
        setSoundControl(SOUND_CONTROL_DEVICE.find(option => option.value === soundControl?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [SOUND_CONTROL_DEVICE])

    useEffect(()=> {
        setScreenSaver(SCREEN_CONTROL_LIST.find(option => option.value === screenSaver?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [SCREEN_CONTROL_LIST])

    useEffect(() => {
        setOutputNumber(outputNumberOptionList.find(option => option.value === outputNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outputNumberOptionList]);

    useEffect(() => {
        setRgbListOptionRed(RGB_LIST.find(option => option.value === rgbListOptionRed?.value) || null)
        setRgbListOptionGreen(RGB_LIST.find(option => option.value === rgbListOptionGreen?.value) || null)
        setRgbListOptionBlue(RGB_LIST.find(option => option.value === rgbListOptionBlue?.value) || null)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [RGB_LIST])

    useEffect(() => {
        setDownOutputNumber(outputNumberOptionList.find(option => option.value === downOutputNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [outputNumberOptionList]);

    /*useEffect(() => {
        setIsRemoteControl(bridgeDevice?.value?.type_id !== BRIDGE_DEVICE_TYPE_ID_FRAME && false)
    }, [bridgeDevice])*/

    useEffect(() => {
        setNavigateToFrameEditor(!controlType?.value?.show_on_frame_ui && false)
    }, [controlType])

    useEffect(()=> {
        setFancoilOutputOption(outputNumberOptionList.find(option => option.value === fancoilOutputOption?.value) || null)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[outputNumberOptionList])

    useEffect(() => {
        setFancoilFanType(FANCOIL_FAN_TYPE_LIST.find(option => option.value === fancoilFanType?.value) || null)
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [FANCOIL_FAN_TYPE_LIST])

    useEffect(()=> {
        setFancoilFan1Option(outputNumberOptionList.find(option => option.value === fancoilFan1Option?.value) || null)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[outputNumberOptionList])

    useEffect(()=> {
        setFancoilFan2Option(outputNumberOptionList.find(option => option.value === fancoilFan2Option?.value) || null)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[outputNumberOptionList])

    useEffect(()=> {
        setFancoilFan3Option(outputNumberOptionList.find(option => option.value === fancoilFan3Option?.value) || null)

        // eslint-disable-next-line react-hooks/exhaustive-deps
    },[outputNumberOptionList])

    useEffect(() => {
        setDoubledOutputNumber(doubledOutputNumberOptionList.find(option => option.value === doubledOutputNumber?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [doubledOutputNumberOptionList]);

    useEffect(() => {
        setAlarmControlOption(alarmControlOptionList.find(option => option.value === alarmControlOption?.value) || null);

        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [alarmControlOptionList]);

    useEffect(() => {
        if (thermostatBridgeDeviceType?.id == null) {
            return;
        }

        if (thermostatBridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_ST) {
            setHeatingModeOption(HEATING_MODE_LIST[0]);
        }
    }, [HEATING_MODE_LIST, thermostatBridgeDeviceType?.id]);

    useEffect(() => {
        if (!isBridgeDeviceTypeIO) {
            setThermostatBridgeDevice(thermostatBridgeDeviceOptionList.find(b => b.value.id === bridgeDevice.value.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [bridgeDevice?.value?.id]);


    //REMOTE CONTROL QUERY
    useEffect(() => {
        if(selectedRemoteControl?.parameters?.['is_remote_control']){
            setBridgeDevice(bridgeDeviceOptionList.find(bridgeDeviceOption => bridgeDeviceOption.value.id === selectedRemoteControl?.bridge_device_id) || null)
        }
    }, [selectedRemoteControl, bridgeDeviceOptionList])


   useEffect(() => {
        if(selectedRemoteControl?.parameters?.['is_remote_control']){
            setControlBridgeDevice(remoteBridgeDeviceOptionList.find(bridgeDeviceOption => bridgeDeviceOption.value.id === bridgeDeviceSet[controlSet[selectedRemoteControl?.control_id]?.bridge_device_id]?.id || null)
            )
        }
    }, [selectedRemoteControl, remoteBridgeDeviceOptionList, bridgeDeviceSet, controlSet])


    useEffect(() => {
        if(selectedRemoteControl?.parameters?.['is_remote_control']){
            setControl(controlOptionList.find(option => option.value.id === selectedRemoteControl?.control_id) || null)
        }
    }, [selectedRemoteControl, controlOptionList])

    useEffect(() => {
        if(selectedRemoteControl?.parameters?.['is_remote_control']){
            setName(selectedRemoteControl?.parameters?.['remote_control_name'] || '')
        }
    }, [selectedRemoteControl])
    //REMOTE CONTROL QUERY

    useEffect(() => {
        if (!notificationReverse) {
            setPickedTimeStringBeginning("")
            setPickedTimeStringFinish("")
        }
    }, [notificationReverse, pickedTimeStringBeginning])

    //todo modifiye sts button for sts scenario
    const stpUIEntityList = useSelector((state) => state.control.stpUIList);
    const deviceControlStpUI = stpUIEntityList.filter(control => control.bridge_device_id === bridgeDevice?.value.id);
    const rowValuesStpUI = deviceControlStpUI.map(control => control.row_number);
    const defaultRowValues = buttonNumberOptionList.map((option) => option.value);
    const newrowValuesStpUI = rowValuesStpUI.map(num => num - 1);
    const matchingRowValues = defaultRowValues.filter((buttonNumber) => !newrowValuesStpUI.includes(buttonNumber));
    const remainingOptionsWithLabel = matchingRowValues.map(option => ({value: option , label: `${option+1}`}));
    /*
    useEffect(()=> {
        console.log(bridgeDevice?.value.id)
    },[bridgeDevice])
    */
    return (
        <PasswordContainer requiredRole="user" isFullscreen={true}>
            <SettingsPageContainer
                className="service-edit"
                buttons={buttons}
                backUrl={BACK_URL}
                currentInput={currentInput}
                currentNumpadInput={currentNumpadInput}
                currentNumpadExtendedInput={currentNumpadExtendedInput}>
                <div className="content-divider">
                    <div className="content-wrapper">
                        <SettingInputComponent
                            placeholder={t('write_control_name')}
                            label={t('control_name')}
                            value={name}
                            setValue={setName}
                            setActiveInput={setActiveInput}
                            onBlur={onBlur}/>
                        <SettingSelectComponent
                            label={t('device')}
                            options={bridgeDeviceOptionList}
                            onChange={setBridgeDevice}
                            value={bridgeDevice}/>
                        {
                            renderIf(bridgeDevice?.value?.type_id === BRIDGE_DEVICE_TYPE_ID_FRAME)(() => (
                                <>
                                <SettingCheckBoxComponent label={t('is_remote_control')} value={isRemoteControl} set={()=> setIsRemoteControl(!isRemoteControl)}/>
                                {
                                    renderIf(isRemoteControl)(() => (
                                        <>
                                            <SettingSelectComponent
                                                label={t('device_to_control')}
                                                options={remoteBridgeDeviceOptionList}
                                                onChange={setControlBridgeDevice}
                                                value={controlBridgeDevice}/>
                                                {
                                                    renderIf(controlBridgeDevice)(() => (
                                                        <SettingSelectComponent
                                                            label={t('control')}
                                                            options={controlOptionList}
                                                            onChange={setControl}
                                                            value={control}/>
                                                    ))
                                                }

                                        </>
                                    ))
                                }
                                </>
                            ))
                        }
                        {
                            renderIf(bridgeDevice && (bridgeDevice?.value?.type_id !== BRIDGE_DEVICE_TYPE_ID_FRAME || !isRemoteControl))(() =>
                                <>
                                    <SettingSelectComponent
                                        label={t('control_type')}
                                        options={controlTypeOptionList}
                                        onChange={setControlType}
                                        value={controlType}
                                    />
                                    {
                                        renderIf(controlType?.value?.id === CONTROL_TYPE_ID_SENSOR_ENERGY)(() => (
                                            <SettingSelectComponent
                                                label={t('alarm_disabled_value')}
                                                options={openCloseOptionList}
                                                onChange={setAlarmDisableValue}
                                                value={alarmDisableValue}
                                            />
                                        ))
                                    }
                                    {
                                        renderIf(controlType?.value?.id === CONTROL_TYPE_ID_EXTERNAL_ALARM)(() => (
                                            <SettingSelectComponent
                                                label={t('alarm_armed_value')}
                                                options={openCloseOptionList}
                                                onChange={setAlarmArmedValue}
                                                value={alarmArmedValue}
                                            />
                                        ))
                                    } 
                                    {
                                        renderIf(controlType?.value?.id === CONTROL_TYPE_ID_VRF)(()=>(
                                            <SettingInputComponent
                                                placeholder={t('write_unit_id')}
                                                label={t('unit_id')}
                                                value={unitID}
                                                setValue={setUnitID}
                                                setActiveInput={setActiveInput}
                                                onBlur={onBlur}/>
                                            ))
                                    }
                                    {
                                        renderIf(controlType?.value?.id === SOUND_PLAYED_TYPE_ID)(() =>
                                            <>
                                                <SettingSelectComponent
                                                    label={'Ses Adı'}
                                                    options={SOUND_CONTROL_DEVICE}
                                                    onChange={setSoundControl}
                                                    value={soundControl}/>
                                            </>,
                                        )
                                    }
                                    {
                                        renderIf(controlType?.value?.id === CONTROL_TYPE_SCREEN_SAVER)(() =>
                                            <>
                                                <SettingSelectComponent
                                                    label={t('type')}
                                                    options={SCREEN_CONTROL_LIST}
                                                    onChange={setScreenSaver}
                                                    value={screenSaver}/>
                                            </>,
                                        )
                                    }
                                    {
                                        renderIf(isBridgeDeviceTypeIO && (controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT || controlType?.value?.id === CONTROL_TYPE_ID_FANCOIL || controlType?.value?.id === CONTROL_TYPE_ADV_THERMOSTAT))(() =>
                                            <>
                                                <SettingSelectComponent
                                                    label={t('sensor_device')}
                                                    options={thermostatOptionList}
                                                    onChange={setThermostatBridgeDevice}
                                                    value={thermostatBridgeDevice}/>
                                            </>,
                                        )
                                    }
                                    {
                                        renderIf(thermostatBridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_ST && /*thermostatBridgeDevice?.value && */(controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT || controlType?.value?.id === CONTROL_TYPE_ADV_THERMOSTAT || (isBridgeDeviceTypeIO && controlType?.value?.id === CONTROL_TYPE_ID_FANCOIL)))(() =>
                                            <SettingSelectComponent
                                                label={t('mode')}
                                                options={HEATING_MODE_LIST}
                                                onChange={setHeatingModeOption}
                                                value={heatingModeOption}/>,
                                        )
                                    }
                                    {
                                        renderIf(!isBridgeDeviceTypeIO && controlType?.value?.id === CONTROL_TYPE_ID_FANCOIL)(() =>
                                        <>
                                            <SettingSelectComponent
                                                label={'Mod'}
                                                options={FANCOIL_HEATING_MODE_LIST}
                                                onChange={setFancoilHeatingModeOption}
                                                value={fancoilHeatingModeOption}/>
                                                {
                                                    renderIf(bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX)(() => (
                                                        <SettingSelectComponent
                                                            label={t('OUTPUT_NO')}
                                                            options={outputNumberOptionList}
                                                            onChange={setFancoilOutputOption}
                                                            value={fancoilOutputOption}/>
                                                        ))
                                                }

                                        </>
                                        )
                                    }
                                    {
                                    renderIf((isBridgeDeviceTypeIO && controlType?.value?.id === CONTROL_TYPE_ID_FANCOIL) || ((isBridgeDeviceTypeIO && isThermostatBridgeDeviceIO) && (controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT || controlType?.value?.id === CONTROL_TYPE_ADV_THERMOSTAT)))(()=> (
                                            <>
                                            {
                                                renderIf(heatingModeOption.value === 1 || heatingModeOption.value === 3)(() =>
                                                    <SettingSelectComponent
                                                        label={t('output_no_heating')}
                                                        options={outputNumberOptionList}
                                                        onChange={setOutputNumber}
                                                        value={outputNumber}/>,
                                                    )
                                            }
                                            {
                                                renderIf(heatingModeOption.value === 2 || heatingModeOption.value === 3)(() =>
                                                    <SettingSelectComponent
                                                        label={t('output_no_cooling')}
                                                        options={outputNumberOptionList}
                                                        onChange={setDownOutputNumber}
                                                        value={downOutputNumber}/>,
                                                    )
                                            }
                                            </>
                                        ))
                                    }
                                    {
                                        renderIf(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_CCM && controlType?.value?.id === CONTROL_TYPE_ID_FANCOIL)(()=> (
                                            <>
                                                <SettingSelectComponent
                                                    label={t("control_edit_page.fan_type")}
                                                    options={FANCOIL_FAN_TYPE_LIST}
                                                    onChange={setFancoilFanType}
                                                    value={fancoilFanType}/>
                                            </>
                                        ))
                                    }
                                    {
                                        renderIf(controlType?.value.id === CONTROL_TYPE_ID_ALARM)(() =>
                                            <>
                                                <SettingSelectComponent
                                                    label={t('control')}
                                                    options={alarmControlOptionList}
                                                    onChange={setAlarmControlOption}
                                                    value={alarmControlOption}/>
                                                <SettingSelectComponent
                                                    label={t('siren')}
                                                    options={SIREN_LIST}
                                                    onChange={setAlarmSirenOption}
                                                    value={alarmSirenOption}/>
                                                <SettingSelectComponent
                                                    label={t('alarm_level')}
                                                    options={ALARM_LEVEL_LIST}
                                                    onChange={setAlarmLevelOption}
                                                    value={alarmLevelOption}/>
                                                <SettingInputComponent
                                                    placeholder={t('alarm_filter_duration')}
                                                    label={t('filter_duration_sn')}
                                                    value={alarmDelaySeconds}
                                                    setValue={setAlarmDelaySeconds}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>, () =>
                                            renderIf((controlType?.value?.render_type === 'three_phase' || controlType?.value?.render_type === 'two_phase') && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX)(() =>
                                                [
                                                    renderIf(isBridgeDeviceTypeIO)(() =>
                                                        <Fragment key="output_no_io">
                                                            <SettingSelectComponent
                                                                label={t('output_no_up')}
                                                                options={outputNumberOptionList}
                                                                onChange={setOutputNumber}
                                                                value={outputNumber}/>
                                                            <SettingSelectComponent
                                                                label={t('output_no_down')}
                                                                options={outputNumberOptionList}
                                                                onChange={setDownOutputNumber}
                                                                value={downOutputNumber}/>
                                                        </Fragment>, () =>
                                                        <SettingSelectComponent
                                                            key="output_no_glass"
                                                            label={t('output_no')}
                                                            options={doubledOutputNumberOptionList}
                                                            onChange={setDoubledOutputNumber}
                                                            value={doubledOutputNumber}/>,
                                                    ),
                                                    <SettingInputComponent
                                                        key="output_duration"
                                                        placeholder={t('write_output_duration_limit')}
                                                        label={t('output_duration_limit')}
                                                        value={outputDurationLimitSeconds}
                                                        setValue={setOutputDurationLimitSeconds}
                                                        setActiveInput={setActiveNumpadInput}
                                                        onBlur={onBlur}/>,
                                                ], () =>
                                                renderIf(!(isBridgeDeviceTypeIO && isThermostatBridgeDeviceIO) && controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX)(() =>
                                                    renderIf(/*thermostatBridgeDevice?.value*/controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT)(() =>
                                                        <>
                                                            {
                                                                renderIf(heatingModeOption.value === 1 || heatingModeOption.value === 3)(() =>
                                                                    <SettingSelectComponent
                                                                        label={t('output_no_heating')}
                                                                        options={outputNumberOptionList}
                                                                        onChange={setOutputNumber}
                                                                        value={outputNumber}/>,
                                                                )
                                                            }
                                                            {
                                                                renderIf(heatingModeOption.value === 2 || heatingModeOption.value === 3)(() =>
                                                                    <SettingSelectComponent
                                                                        label={t('output_no_cooling')}
                                                                        options={outputNumberOptionList}
                                                                        onChange={setDownOutputNumber}
                                                                        value={downOutputNumber}/>,
                                                                )
                                                            }
                                                        </>,
                                                    ), () =>
                                                    <>
                                                        {
                                                            renderIf(controlType?.value?.id !== CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN && controlType?.value?.id !== CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON && controlType?.value?.id !== CONTROL_TYPE_SCREEN_SAVER && controlType?.value?.id !== SOUND_PLAYED_TYPE_ID && controlType?.value?.id !== SOUND_LEVEL_TYPE_ID && controlType?.value &&  controlType?.value?.id !== CONTROL_TYPE_ID_FANCOIL && controlType?.value?.id !== CONTROL_TYPE_ID_THERMOSTAT && controlType?.value?.id !== CONTROL_TYPE_GENERAL_TEMPERATURE && bridgeDeviceType.id !== BRIDGE_DEVICE_TYPE_ID_KNX && controlType?.value?.id !== CONTROL_TYPE_ID_VRF && controlType?.value?.id !== CONTROL_TYPE_ADV_THERMOSTAT)(() =>
                                                                <SettingSelectComponent
                                                                    label={t('output_no')}
                                                                    options={outputNumberOptionList}
                                                                    onChange={setOutputNumber}
                                                                    value={outputNumber}/>,
                                                            )
                                                        }
                                                    </>,
                                                ),
                                            ),
                                        )
                                    }
                                    {/* KNX DEVICE */}
                                    {
                                        renderIf(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX)(()=> (
                                            <>
                                            {
                                                renderIf(controlType?.value?.id === CONTROL_TYPE_ID_FANCOIL)(() => (
                                                    <>
                                                        <SettingInputComponent
                                                            placeholder={t('enter_cool_mode_feedback')}
                                                            label={t('cool_mode_feedback')}
                                                            value={fancoilCoolingModeFeedback}
                                                            setValue={setFancoilCoolingModeFeedback}
                                                            setActiveInput={setActiveInput}
                                                            onBlur={onBlur}/>
                                                        <SettingInputComponent
                                                            placeholder={t('enter_heat_mode_address')}
                                                            label={t('heat_mode_feedback')}
                                                            value={fancoilHeatingModeFeedback}
                                                            setValue={setFancoilHeatingModeFeedback}
                                                            setActiveInput={setActiveInput}
                                                            onBlur={onBlur}/>
                                                        <SettingInputComponent
                                                            placeholder={t('enter_mode_feedback_address')}
                                                            label={t('mode_feedback_address')}
                                                            value={fancoilModeFeedback}
                                                            setValue={setFancoilModeFeedback}
                                                            setActiveInput={setActiveInput}
                                                            onBlur={onBlur}/>
                                                        <SettingInputComponent
                                                            placeholder={t('enter_mode_output_address')}
                                                            label={t('mode_output_address')}
                                                            value={fancoilModeOutput}
                                                            setValue={setFancoilModeOutput}
                                                            setActiveInput={setActiveInput}
                                                            onBlur={onBlur}/>
                                                    </>
                                                ))
                                            }
                                            {
                                                renderIf(controlType?.value?.id === CONTROL_TYPE_ID_LIGHT)(() => (
                                                    <>
                                                        <SettingInputComponent
                                                            placeholder={t('enter_set_address')}
                                                            label={t('set_address')}
                                                            value={knxLampOutputAddress}
                                                            setValue={setKnxLampOutputAddress}
                                                            setActiveInput={setActiveInput}
                                                            onBlur={onBlur}/>
                                                        <SettingInputComponent
                                                            placeholder={t('enter_feedback_address')}
                                                            label={t('feedback_address')}
                                                            value={knxLampFeedbackAddress}
                                                            setValue={setKnxLampFeedbackAddress}
                                                            setActiveInput={setActiveInput}
                                                            onBlur={onBlur}/>
                                                    </>
                                                ))
                                            }
                                            {
                                                renderIf(controlType?.value?.id === CONTROL_TYPE_ID_ANALOG)(() => (
                                                    <>
                                                        <SettingInputComponent
                                                            placeholder={t('enter_set_address')}
                                                            label={t('set_address')}
                                                            value={knxDimmerOutputAddress}
                                                            setValue={setKnxDimmerOutputAddress}
                                                            setActiveInput={setActiveInput}
                                                            onBlur={onBlur}/>
                                                        <SettingInputComponent
                                                            placeholder={t('enter_feedback_address')}
                                                            label={t('feedback_address')}
                                                            value={knxDimmerFeedbackAddress}
                                                            setValue={setKnxDimmerFeedbackAddress}
                                                            setActiveInput={setActiveInput}
                                                            onBlur={onBlur}/>
                                                    </>
                                                ))
                                            }
                                            {
                                                renderIf(controlType?.value?.id === CONTROL_TYPE_ID_CURTAIN)(() => (
                                                    <>
                                                        <SettingInputComponent
                                                            placeholder={t('enter_stop_address')}
                                                            label={t('stop_address')}
                                                            value={knxCurtainStopAddress}
                                                            setValue={setKnxCurtainStopAddress}
                                                            setActiveInput={setActiveInput}
                                                            onBlur={onBlur}/>
                                                        <SettingInputComponent
                                                            placeholder={t('enter_updown_address')}
                                                            label={t('updown_address')}
                                                            value={knxCurtainUpDownAddress}
                                                            setValue={setKnxCurtainUpDownAddress}
                                                            setActiveInput={setActiveInput}
                                                            onBlur={onBlur}/>
                                                    </>
                                                ))
                                            }
                                            {
                                                renderIf(controlType?.value?.id === CONTROL_TYPE_ID_PLUG)(() => (
                                                    <>
                                                        <SettingInputComponent
                                                            placeholder={t('enter_set_address')}
                                                            label={t('set_address')}
                                                            value={knxPlugOutputAddress}
                                                            setValue={setKnxPlugOutputAddress}
                                                            setActiveInput={setActiveInput}
                                                            onBlur={onBlur}/>
                                                        <SettingInputComponent
                                                            placeholder={t('enter_feedback_address')}
                                                            label={t('feedback_address')}
                                                            value={knxPlugFeedbackAddress}
                                                            setValue={setKnxPlugFeedbackAddress}
                                                            setActiveInput={setActiveInput}
                                                            onBlur={onBlur}/>
                                                    </>
                                                ))
                                            }
                                            {
                                                renderIf(controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT)(() => (
                                                    <>
                                                        {
                                                            renderIf(heatingModeOption?.value === 1 || heatingModeOption?.value === 3)(() => (
                                                                <SettingInputComponent
                                                                    placeholder={t('enter_heating_feedback_address')}
                                                                    label={t('heating_feedback_address')}
                                                                    value={knxThermostatHeatingFeedbackAddress}
                                                                    setValue={setKnxThermostatHeatingFeedbackAddress}
                                                                    setActiveInput={setActiveInput}
                                                                    onBlur={onBlur}/>
                                                                ))
                                                        }
                                                        {
                                                            renderIf(heatingModeOption?.value === 2 || heatingModeOption?.value === 3)(() => (
                                                                <SettingInputComponent
                                                                    placeholder={t('enter_cooling_feedback_address')}
                                                                    label={t('cooling_feedback_address')}
                                                                    value={knxThermostatCoolingFeedbackAddress}
                                                                    setValue={setKnxThermostatCoolingFeedbackAddress}
                                                                    setActiveInput={setActiveInput}
                                                                    onBlur={onBlur}/>))
                                                        }
                                                    </>
                                                ))
                                            }
                                            </>
                                        ))
                                    }
                                    {/* KNX DEVICE */}
                                    {
                                        renderIf(controlType?.value?.id === CONTROL_TYPE_ID_THREE_ANALOG)(() =>
                                            <>
                                                <SettingSelectComponent
                                                    label={t('control_edit_page.red_output')}
                                                    options={RGB_LIST}
                                                    onChange={setRgbListOptionRed}
                                                    value={rgbListOptionRed}
                                                />
                                                <SettingSelectComponent
                                                    label={t('control_edit_page.green_output')}
                                                    options={RGB_LIST}
                                                    onChange={setRgbListOptionGreen}
                                                    value={rgbListOptionGreen}
                                                />
                                                <SettingSelectComponent
                                                    label={t('control_edit_page.blue_output')}
                                                    options={RGB_LIST}
                                                    onChange={setRgbListOptionBlue}
                                                    value={rgbListOptionBlue}
                                                />
                                            </>
                                        )
                                    }
                                    {
                                        renderIf(isBridgeDeviceTypeIO && controlType?.value?.render_type === 'pulse')(() =>
                                            <SettingInputComponent
                                                placeholder={t('write_pulse_duration')}
                                                label={bridgeDevice?.value?.type_id === DeviceTypes.ETHERNET_IO ? 'Pulse T. (*100ms)' : t('pulse_duration')}
                                                value={pulseDuration}
                                                setValue={setPulseDuration}
                                                setActiveInput={setActiveNumpadExtendedInput}
                                                onBlur={onBlur}/>,
                                        )
                                    }
                                    {
                                        renderIf((controlType?.value.id === CONTROL_TYPE_ID_DIMMER || controlType?.value.id === CONTROL_TYPE_ID_ANALOG) && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX)(() =>
                                            <>
                                                <SettingInputComponent
                                                    placeholder={t('write_maximum_value')}
                                                    label={t('maximum_value')}
                                                    value={maxValue}
                                                    setValue={setMaxValue}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                                <SettingInputComponent
                                                    placeholder={t('write_minimum_value')}
                                                    label={t('minimum_value')}
                                                    value={minValue}
                                                    setValue={setMinValue}
                                                    setActiveInput={setActiveNumpadInput}
                                                    onBlur={onBlur}/>
                                            </>,
                                        )
                                    }
                                    {
                                        renderIf(controlType?.value.id === CONTROL_TYPE_ID_SIREN)(() =>
                                            <SettingSelectComponent
                                                label={t('alarm_level')}
                                                options={SIREN_LEVEL_LIST}
                                                onChange={setSirenLevelOption}
                                                value={sirenLevelOption}/>,
                                        )
                                    }
                                </>,
                            )
                        }
                    </div>
                    <div className="content-wrapper">
                        {
                            renderIf(bridgeDevice?.value?.type_id !== BRIDGE_DEVICE_TYPE_ID_FRAME || !isRemoteControl)(() => (
                            <>
                        {
                            renderIf(controlType?.value?.id !== CONTROL_TYPE_SCREEN_SAVER && controlType?.value?.id !== SOUND_PLAYED_TYPE_ID && controlType?.value?.id !== SOUND_LEVEL_TYPE_ID)(() =>
                            <>
                            <SettingSelectComponent
                                label={t('area')}
                                options={areaOptionList}
                                onChange={setAreaOption}
                                value={areaOption}/>
                                {
                                    renderIf(controlType?.value?.id !== CONTROL_TYPE_ADV_THERMOSTAT)(()=> (
                                        <>
                                        {/*<SettingCheckBoxComponent
                                            label={'Bildirim'}
                                            set={setNotificationReverseCheckbox}
                                    value={notificationReverse}/>*/}
                                {
                                    renderIf(notificationReverse)(()=>
                                    <>
                                        <SettingInputComponent
                                            placeholder={t('set_time')}
                                            label={'Başlangıç'}
                                            value={pickedTimeStringBeginning}
                                            setValue={setPickedTimeStringBeginning}
                                            setActiveInput={null}
                                            type='time'/>
                                        <SettingInputComponent
                                            placeholder={t('set_time')}
                                            label={'Bitiş'}
                                            value={pickedTimeStringFinish}
                                            setValue={setPickedTimeStringFinish}
                                            setActiveInput={null}
                                            type='time'/>
                                    </>
                                    )}
                                    </>
                                    ))
                                }
                            </>)}
                        {
                            renderIf(controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT && bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX)(() => (
                                <>
                                    <SettingInputComponent
                                        placeholder={t('enter_current_feedback_address')}
                                        label={t('current_feedback_address')}
                                        value={knxThermostatCurrentTempFeedbackAddress}
                                        setValue={setKnxThermostatCurrentTempFeedbackAddress}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('enter_setpoint_output_address')}
                                        label={t('setpoint_output_address')}
                                        value={knxThermostatSetpointOutputAddress}
                                        setValue={setKnxThermostatSetpointOutputAddress}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('enter_setpoint_feedback_address')}
                                        label={t('setpoint_feedback_address')}
                                        value={knxThermostatCurrentSetpointFeedbackAddress}
                                        setValue={setKnxThermostatCurrentSetpointFeedbackAddress}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('enter_mode_output_address')}
                                        label={t('mode_output_address')}
                                        value={knxThermostatModeOutputAddress}
                                        setValue={setKnxThermostatModeOutputAddress}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('enter_mode_feedback_address')}
                                        label={t('mode_feedback_address')}
                                        value={knxThermostatModeFeedbackAddress}
                                        setValue={setKnxThermostatModeFeedbackAddress}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                </>))
                        }
                        {
                            renderIf(controlType?.value?.id === CONTROL_TYPE_ID_FANCOIL && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_KNX)(() =>
                                <>
                                    <SettingSelectComponent
                                        label={t('control_edit_page.fan_output_1')}
                                        options={outputNumberOptionList}
                                        onChange={setFancoilFan1Option}
                                        value={fancoilFan1Option}/>
                                    <SettingSelectComponent
                                        label={t('control_edit_page.fan_output_2')}
                                        options={outputNumberOptionList}
                                        onChange={setFancoilFan2Option}
                                        value={fancoilFan2Option}/>
                                    <SettingSelectComponent
                                        label={t('control_edit_page.fan_output_3')}
                                        options={outputNumberOptionList}
                                        onChange={setFancoilFan3Option}
                                        value={fancoilFan3Option}/>
                                </>
                            )
                        }
                        {
                            renderIf(controlType?.value?.id === CONTROL_TYPE_ID_FANCOIL && bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_KNX)(() => (
                                <>
                                    <SettingInputComponent
                                        placeholder={t('enter_setpoint_feedback_address')}
                                        label={t('setpoint_feedback_address')}
                                        value={fancoilCurrentSetpointFeedback}
                                        setValue={setFancoilCurrentSetpointFeedback}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('enter_setpoint_output_address')}
                                        label={t('setpoint_output_address')}
                                        value={fancoilCurrentSetpointOutput}
                                        setValue={setFancoilCurrentSetpointOutput}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('enter_temp_feedback_address')}
                                        label={t('temp_feedback_address')}
                                        value={fancoilCurrentTempFeedback}
                                        setValue={setFancoilCurrentTempFeedback}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('enter_auto_fan_feedback_address')}
                                        label={t('auto_fan_feedback_address')}
                                        value={fancoilAutoFanFeedback}
                                        setValue={setFancoilAutoFanFeedback}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('enter_auto_fan_output_address')}
                                        label={t('auto_fan_output_address')}
                                        value={fancoilAutoFanOutput}
                                        setValue={setFancoilAutoFanOutput}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('enter_fan_speed_feedback_address')}
                                        label={t('fan_speed_feedback_address')}
                                        value={fancoilFanSpeedFeedback}
                                        setValue={setFancoilFanSpeedFeedback}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('enter_fan_speed_output_address')}
                                        label={t('fan_speed_output_address')}
                                        value={fancoilFanSpeedOutput}
                                        setValue={setFancoilFanSpeedOutput}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('enter_status_feedback_address')}
                                        label={t('status_feedback_address')}
                                        value={fancoilStatusFeedback}
                                        setValue={setFancoilStatusFeedback}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                    <SettingInputComponent
                                        placeholder={t('enter_status_output_address')}
                                        label={t('status_output_address')}
                                        value={fancoilStatusOutput}
                                        setValue={setFancoilStatusOutput}
                                        setActiveInput={setActiveInput}
                                        onBlur={onBlur}/>
                                </>
                            ))
                        }

                        {
                            renderIf(controlType?.value?.id !== CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN && controlType?.value?.id !== CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON && controlType?.value?.id !== CONTROL_TYPE_SCREEN_SAVER && controlType?.value?.id !== SOUND_PLAYED_TYPE_ID && controlType?.value?.id !== SOUND_LEVEL_TYPE_ID && controlType != null && isBridgeDeviceTypeIO && bridgeDevice?.value?.type_id !== DeviceTypes.ETHERNET_IO && controlType?.value?.id !== CONTROL_TYPE_ID_THREE_ANALOG && controlType?.value?.id !== CONTROL_TYPE_GENERAL_TEMPERATURE)(() =>
                                <SettingCheckBoxComponent
                                    label={t('output_reverse')}
                                    set={setOutputReverseCheckbox}
                                    value={outputReverse}/>,
                            )
                        }
                        {
                            renderIf(controlType?.value?.id !== CONTROL_TYPE_ID_MKK && controlType?.value?.id !== CONTROL_TYPE_SCREEN_SAVER && controlType?.value?.id !== SOUND_PLAYED_TYPE_ID && controlType?.value?.id !== SOUND_LEVEL_TYPE_ID && controlType?.value != null &&
                                controlType.value.id !== CONTROL_TYPE_ID_ALARM && controlType.value.id !== CONTROL_TYPE_ID_SIREN && /*controlType?.value?.id !== CONTROL_TYPE_ADV_THERMOSTAT && */controlType?.value?.id !== CONTROL_TYPE_GENERAL_TEMPERATURE && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_EIO && controlType?.value?.id !== CONTROL_TYPE_ID_VRF && controlType?.value?.id !== CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN && controlType?.value?.id !== CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON)(() =>

                                renderIf(isBridgeDeviceTypeIO && controlType.value.id !== CONTROL_TYPE_ID_THERMOSTAT && controlType?.value?.render_type !== 'pulse')(() =>
                                    <>
                                        <SettingCheckBoxComponent
                                            label={t('remember_last_status')}
                                            set={setRememberLastValueCheckbox}
                                            value={
                                                controlType?.value?.id === CONTROL_TYPE_ID_SENSOR_ENERGY ||
                                                    controlType?.value?.id === CONTROL_TYPE_ID_EXTERNAL_ALARM
                                                    ? true : rememberLastValue
                                            }
                                        />
                                        {
                                            renderIf(
                                                !rememberLastValue &&
                                                controlType?.value?.id !== CONTROL_TYPE_ID_THREE_ANALOG &&
                                                controlType?.value?.id !== CONTROL_TYPE_ADV_THERMOSTAT &&
                                                controlType?.value?.id !== CONTROL_TYPE_ID_SENSOR_ENERGY &&
                                                controlType?.value?.id !== CONTROL_TYPE_ID_EXTERNAL_ALARM
                                            )(() =>
                                                <SettingSelectComponent
                                                    label={t('default_value')}
                                                    options={defaultValueOptionList}
                                                    onChange={setDefaultValueOption}
                                                    value={defaultValueOption}/>,
                                            )
                                        }
                                    </>, () =>
                                    <>
                                        {
                                            renderIf(bridgeDeviceType?.button_count > 0 && controlType?.value?.id !== CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON)(() =>
                                                <SettingSelectComponent
                                                    label={t('button_number')}
                                                    options={remainingOptionsWithLabel}
                                                    onChange={setButtonNumber}
                                                    value={buttonNumber}/>,
                                            )
                                        }
                                    </>,
                                ))
                        }
                        {
                            renderIf(!isBridgeDeviceTypeIO && controlType?.value?.render_type === 'simple')(() =>
                                <SettingCheckBoxComponent
                                    label={t('output_reverse')}
                                    set={setOutputReverseCheckbox}
                                    value={outputReverse}/>,
                            )
                        }
                        {
                            renderIf(
                                !navigateToFrameEditor &&
                                isBridgeDeviceTypeIO &&
                                selected?.id == null &&
                                ![
                                    BRIDGE_DEVICE_TYPE_ID_CCM2312,
                                    BRIDGE_DEVICE_TYPE_ID_CCM2324,
                                    DeviceTypes.ETHERNET_IO
                                ].includes(bridgeDevice?.value?.type_id) &&
                                ![
                                    CONTROL_TYPE_SCREEN_SAVER,
                                    SOUND_PLAYED_TYPE_ID,
                                    SOUND_LEVEL_TYPE_ID,
                                    CONTROL_TYPE_ID_THERMOSTAT,
                                    CONTROL_TYPE_ADV_THERMOSTAT,
                                    CONTROL_TYPE_ID_FANCOIL,
                                    CONTROL_TYPE_GENERAL_TEMPERATURE,
                                    CONTROL_TYPE_ID_GLASS_DEVICE_SCREEN,
                                    CONTROL_TYPE_ID_GLASS_DEVICE_BUTTON,
                                    CONTROL_TYPE_ID_SENSOR_ENERGY,
                                    CONTROL_TYPE_ID_EXTERNAL_ALARM
                                ].includes(controlType?.value?.id)
                            )(() =>
                                <SettingCheckBoxComponent
                                    label={t('add_input')}
                                    set={setAddInputCheckbox}
                                    value={addInput}/>,
                            )
                        }
                        {
                            renderIf(selected?.id == null &&
                                (bridgeDeviceType?.page_count > 0
                                    && bridgeDeviceType?.id !== '6' && controlType?.value?.id !== '202' && controlType?.value?.id !== '203' && bridgeDeviceType?.id !== BRIDGE_DEVICE_TYPE_ID_FRAME
                                    /*||
                                    (isBridgeDeviceTypeIO && controlType?.value?.id === CONTROL_TYPE_ID_THERMOSTAT && !isThermostatBridgeDeviceIO)*/))(() =>
                                <SettingCheckBoxComponent
                                    label={t(bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_STS_PLUS ? 'go_to_sts_plus_editor' : 'go_to_stp_editor')}
                                    set={setNavigateStpEditorCheckbox}
                                    value={navigateToSTPEditor}/>,
                            )
                        }
                        <SettingCheckBoxComponent
                            label={t('hide_control')}
                            set={setShowControlCheckbox}
                            value={hideControl}/>
                        </>
                        ))
                    }
                    {
                            renderIf(!addInput && (selected?.id == null && selectedRemoteControl?.id == null) &&
                                (bridgeDeviceType?.page_count > 0
                                    && bridgeDeviceType?.id === BRIDGE_DEVICE_TYPE_ID_FRAME && (isRemoteControl ? controlTypeSet[control?.value?.type_id]?.show_on_frame_ui :  controlType?.value?.show_on_frame_ui)))(() =>
                                <SettingCheckBoxComponent
                                    label={t('go_to_frame_editor')}
                                    set={setNavigateFrameEditorCheckbox}
                                    value={navigateToFrameEditor}/>,
                            )
                    }
                      {
                                renderIf(
                                    passwordControlQuery &&
                                    controlType?.value?.id !== CONTROL_TYPE_ID_SENSOR_ENERGY &&
                                    controlType?.value?.id !== CONTROL_TYPE_ID_EXTERNAL_ALARM
                                )(() =>(<SettingCheckBoxComponent
                                    label={t('password_control')}
                                    set={setPasswordControlCheckbox}
                                    value={passwordControl}/>
                                ))
                       }
                    </div>

                </div>
            </SettingsPageContainer>
            {
                renderKeyboard()
            }
            {
                renderNumpadKeyboard()
            }
            {
                renderNumpadExtendedKeyboard()
            }
        </PasswordContainer>
    );
});
